/**
 * UI Configuration API Service
 *
 * This service manages custom field configurations across the application.
 * It provides a dynamic API layer that supports both mock data (for development)
 * and automatic backend integration (for production).
 *
 * Features:
 * - Custom field CRUD operations
 * - Field validation and uniqueness checks
 * - Salesforce compatibility (auto-adds __c suffix)
 * - Statistics and analytics
 * - React Query integration ready
 * - Automatic backend fallback
 *
 * @see UI-1A ticket for full specification
 */

// Import Gobodo service (conditional)
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
let gobodoPageLayoutService: typeof import('../gobodo/gobodoPageLayoutService') | null = null;
if (USE_GOBODO_API) {
  try {
    gobodoPageLayoutService = require('../gobodo/gobodoPageLayoutService');
  } catch (e) {
    console.warn('Failed to load gobodoPageLayoutService:', e);
  }
}

// ===========================
// TYPESCRIPT INTERFACES
// ===========================

/**
 * Available field types for custom fields
 */
export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'url'
  | 'currency'
  | 'percentage';

/**
 * Applications that support custom fields
 */
export type Application =
  | 'global'
  | 'cybersweep'
  | 'credit-repair'
  | 'id-theft'
  | 'credit-development'
  | 'credit-block'
  | 'regal-main';

/**
 * Logical sections within each application
 */
export type FieldSection =
  | 'personal-info'
  | 'contact-info'
  | 'identity-info'
  | 'financial-info'
  | 'case-details'
  | 'dispute-info'
  | 'bureau-info'
  | 'documents'
  | 'metadata'
  | 'custom';

/**
 * Complete custom field definition
 */
export interface CustomField {
  id: string;
  fieldKey: string;              // Unique identifier with __c suffix (e.g., 'ssn__c')
  label: string;                 // Display label
  type: FieldType;               // Field data type
  application: Application;       // Which app this field belongs to
  section: FieldSection;         // Which section within the app
  isRequired: boolean;           // Whether field is mandatory
  isActive: boolean;             // Whether field is currently in use
  isSystem: boolean;             // Whether field is system-defined (non-deletable)
  defaultValue?: string | number | boolean; // Optional default value
  helpText?: string;             // Optional help text for users
  placeholder?: string;          // Optional placeholder text
  validationRules?: {
    minLength?: number;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    pattern?: string;            // Regex pattern for validation
    customValidation?: string;   // Custom validation logic
  };
  options?: Array<{              // For select/multiselect types
    label: string;
    value: string;
    isDefault?: boolean;
  }>;
  displayOrder: number;          // Order in which field appears in UI
  createdAt: string;             // ISO 8601 timestamp
  updatedAt: string;             // ISO 8601 timestamp
  createdBy: string;             // User who created the field
  updatedBy?: string;            // User who last updated the field
}

/**
 * Filter criteria for fetching custom fields
 */
export interface CustomFieldFilters {
  application?: Application;
  section?: FieldSection;
  type?: FieldType;
  isActive?: boolean;
  isSystem?: boolean;
  searchTerm?: string;           // Search in label or fieldKey
}

/**
 * Statistics about custom field usage
 */
export interface FieldStatistics {
  totalFields: number;
  activeFields: number;
  inactiveFields: number;
  systemFields: number;
  customFields: number;
  byApplication: Record<Application, number>;
  byType: Record<FieldType, number>;
  bySection: Record<FieldSection, number>;
  recentlyCreated: CustomField[];  // Last 5 created
  recentlyUpdated: CustomField[];  // Last 5 updated
}

// ============================================
// PAGE LAYOUT TYPES
// ============================================

export interface PageLayoutField {
  fieldId: string;          // References CustomField.id
  label: string;
  fieldKey: string;
  type: FieldType;
  required: boolean;
  placeholder?: string;
  order: number;
  width?: 'full' | 'half' | 'third' | 'quarter';
  helpText?: string;
  piiConfig?: {
    encrypted: boolean;
    encryptionLevel?: 'standard' | 'high' | 'maximum';
    masked: boolean;
    maskingPattern?: string;  // e.g., "***-**-1234" for SSN
    accessRoles?: string[];   // Roles that can view unmasked
  };
  // Phase 5: Flat PII properties for direct access
  pii?: boolean;
  encryption?: 'standard' | 'high' | 'maximum';
  masking?: {
    enabled: boolean;
    pattern: string;
    unmaskRoles?: string[];
  };
  accessRoles?: string[];
}

export interface PageLayoutSection {
  id: string;
  name: string;
  description?: string;
  page: string;             // e.g., 'clients-record', 'cybersweep-audit'
  tab: string;              // e.g., 'overview', 'details', 'history'
  columns: 1 | 2 | 3 | 4;   // Number of columns in section
  order: number;            // Display order on page
  collapsible: boolean;
  defaultCollapsed: boolean;
  visible: boolean;
  fields: PageLayoutField[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface PageLayoutTemplate {
  id: string;
  name: string;
  description?: string;
  application: Application;
  objectTypeKey?: string;   // e.g. Client__c – scopes layout to an object
  isPublic: boolean;        // Can other users see this template?
  isDefault: boolean;       // Is this the default template?
  sections: PageLayoutSection[];
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface PageLayoutVersion {
  id: string;
  templateId: string;
  version: number;
  changes: string;          // Description of changes
  sections: PageLayoutSection[];
  createdAt: string;
  createdBy: string;
}

export interface DynamicTab {
  id: string;
  page: string;             // Parent page (e.g., 'clients-record')
  name: string;             // Tab label
  key: string;              // Tab key for routing
  icon?: string;
  order: number;
  visible: boolean;
  application: Application;
}

// ============================================
// LAYOUT SECTION MANAGEMENT TYPES (UI-2 PHASE 5)
// ============================================

export interface LayoutSection {
  id: string;
  layoutId: string;
  name: string;
  label: string;
  order: number;
  columns: 1 | 2;          // Number of columns (1 or 2)
  collapsible: boolean;     // Can section be collapsed
  collapsed: boolean;       // Is section collapsed by default
  fields: SectionField[];   // Fields in this section
}

export interface SectionField {
  id: string;
  fieldId: string;          // Reference to custom field
  fieldName: string;        // Field API name (e.g., clientName__c)
  fieldLabel: string;       // Display label
  fieldType: string;        // Field type from custom fields
  order: number;            // Order within section
  column: 1 | 2;           // Which column (for 2-column layouts)
  required: boolean;        // Override field's required setting
  readonly: boolean;        // Make field read-only
  width: 'full' | 'half';  // Field width (full row or half)
}

// ============================================
// LAYOUT VISIBILITY RULE TYPES
// ============================================

export type RuleConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'is_empty'
  | 'is_not_empty'
  | 'in_list'
  | 'not_in_list';

export type RuleLogic = 'AND' | 'OR';
export type RuleAction = 'show' | 'hide' | 'require' | 'readonly';
export type RuleTarget = 'section' | 'field';

export interface RuleCondition {
  id: string;
  fieldName: string;           // Field to check
  fieldLabel: string;          // Display label
  operator: RuleConditionOperator;
  value: any;                  // Value to compare against
  valueList?: any[];           // For in_list/not_in_list operators
}

export interface VisibilityRule {
  id: string;
  layoutId: string;
  name: string;
  description?: string;
  enabled: boolean;
  priority: number;            // Lower number = higher priority
  logic: RuleLogic;            // How to combine conditions
  conditions: RuleCondition[]; // What to check
  action: RuleAction;          // What to do
  target: RuleTarget;          // What to affect
  targetId: string;            // Section or field ID
  targetName: string;          // For display
  createdAt: string;
  updatedAt: string;
}

export interface RuleEvaluationResult {
  ruleId: string;
  ruleName: string;
  matched: boolean;
  action: RuleAction;
  target: RuleTarget;
  targetId: string;
  appliedAt: string;
}

// ============================================
// RELATED LIST & QUICK ACTION TYPES (UI-2 PHASE 7)
// ============================================

export type ActionType = 'standard' | 'custom' | 'link' | 'javascript';
export type ActionIcon =
  | '➕' | '✏️' | '📧' | '📞' | '📅' | '💬' | '📄' | '🔗'
  | '✓' | '✕' | '🗑️' | '⚙️' | '🔍' | '📤' | '📥';

export interface RelatedList {
  id: string;
  layoutId: string;
  name: string;                // API name (e.g., 'relatedCases')
  label: string;               // Display label (e.g., 'Related Cases')
  relatedObject: string;       // Object type (e.g., 'case')
  relationshipField: string;   // Field linking to parent (e.g., 'clientId__c')
  order: number;               // Display order
  columns: string[];           // Column field names to show
  sortField?: string;          // Default sort field
  sortDirection?: 'asc' | 'desc';
  rowLimit: number;            // Max rows to display (default: 10)
  showNewButton: boolean;      // Show "New" button
  showViewAllLink: boolean;    // Show "View All" link
  filters?: any;               // Optional filters
  enabled: boolean;
}

export interface QuickAction {
  id: string;
  layoutId: string;
  name: string;                // API name (e.g., 'sendWelcomeEmail')
  label: string;               // Button label (e.g., 'Send Welcome Email')
  type: ActionType;
  order: number;               // Display order
  icon?: ActionIcon;           // Button icon
  description?: string;
  url?: string;                // For link actions
  script?: string;             // For javascript actions
  openInNewWindow?: boolean;   // For link actions
  confirmationMessage?: string; // Show before executing
  successMessage?: string;     // Show after success
  enabled: boolean;
  visibleForRoles?: string[];  // Role-based visibility
}

export interface CustomButton {
  id: string;
  layoutId: string;
  sectionId?: string;          // Optional: attach to specific section
  name: string;
  label: string;
  type: ActionType;
  order: number;
  icon?: ActionIcon;
  description?: string;
  url?: string;
  script?: string;
  style?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  enabled: boolean;
}

// ============================================
// LAYOUT ASSIGNMENT & TESTING TYPES (UI-2 PHASE 8)
// ============================================

export interface LayoutAssignment {
  id: string;
  layoutId: string;
  profileId?: string;          // Assigned to specific profile
  profileName?: string;
  recordType?: string;          // Assigned to record type
  isDefault: boolean;           // Is this the default layout
  priority: number;             // Assignment priority
  createdAt: string;
  updatedAt: string;
}

export interface LayoutPreviewData {
  layoutId: string;
  sections: LayoutSection[];
  visibleSections: string[];    // Section IDs that are visible
  hiddenSections: string[];     // Section IDs that are hidden
  requiredFields: string[];     // Field IDs that are required
  readonlyFields: string[];     // Field IDs that are readonly
  relatedLists: RelatedList[];
  quickActions: QuickAction[];
  appliedRules: string[];       // Rule IDs that were applied
}

export interface LayoutValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  suggestions: string[];
}

export interface ValidationError {
  severity: 'error' | 'critical';
  section: string;              // Which section has the error
  field?: string;               // Specific field (if applicable)
  message: string;
  fix?: string;                 // Suggested fix
}

export interface ValidationWarning {
  severity: 'warning' | 'info';
  section: string;
  field?: string;
  message: string;
}

export interface LayoutCloneRequest {
  sourceLayoutId: string;
  newName: string;
  newLabel: string;
  includeVisibilityRules: boolean;
  includeRelatedLists: boolean;
  includeQuickActions: boolean;
}

export interface LayoutHealthCheck {
  layoutId: string;
  health: 'excellent' | 'good' | 'fair' | 'poor';
  score: number;                // 0-100
  metrics: {
    sectionsCount: number;
    fieldsCount: number;
    rulesCount: number;
    relatedListsCount: number;
    quickActionsCount: number;
    unusedFields: number;
    conflictingRules: number;
  };
  recommendations: string[];
}

// ============================================
// LIST VIEW TYPES
// ============================================

export type ColumnAlignment = 'left' | 'center' | 'right';
export type ColumnDataType = 'text' | 'number' | 'date' | 'boolean' | 'currency' | 'percentage' | 'enum' | 'custom';
export type SortDirection = 'asc' | 'desc';

export interface ListViewColumn {
  id: string;
  fieldKey: string;          // References field key (e.g., 'firstName__c')
  label: string;             // Column header label
  dataType: ColumnDataType;  // Data type for proper formatting
  width: number;             // Column width in pixels or percentage
  minWidth?: number;         // Minimum column width
  sortable: boolean;         // Can column be sorted?
  filterable: boolean;       // Can column be filtered?
  resizable: boolean;        // Can column be resized?
  visible: boolean;          // Is column visible?
  order: number;             // Display order
  alignment: ColumnAlignment;
  format?: string;           // Format string (e.g., 'MM/DD/YYYY' for dates)
}

export interface ListViewFilter {
  fieldKey: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan' | 'between' | 'in';
  value: any;
  logicalOperator?: 'AND' | 'OR';
  caseSensitive?: boolean;
}

export interface ListViewSort {
  fieldKey: string;
  direction: SortDirection;
  priority: number;          // For multi-column sorting
}

export interface ListViewTemplate {
  id: string;
  name: string;
  description?: string;
  application: Application;
  entityType: string;        // e.g., 'clients', 'audits', 'cases', 'users'
  isPublic: boolean;         // Can other users see this template?
  isDefault: boolean;        // Is this the default view for this entity?
  columns: ListViewColumn[];
  defaultSort?: ListViewSort[];
  defaultFilters?: ListViewFilter[];
  rowsPerPage: number;       // Default pagination size
  rowsPerPageOptions: number[]; // Available pagination options
  enableExport: boolean;     // Allow data export?
  exportFormats: ('csv' | 'xlsx' | 'pdf')[];
  enableColumnReorder: boolean;
  enableColumnResize: boolean;
  enableQuickFilters: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface ListViewStatistics {
  totalTemplates: number;
  byApplication: Record<Application, number>;
  byEntityType: Record<string, number>;
  defaultTemplates: ListViewTemplate[];
  recentlyCreated: ListViewTemplate[];
  recentlyUpdated: ListViewTemplate[];
}

// ===========================
// USER MANAGEMENT TYPES
// ===========================

export type UserRole =
  | 'system_admin'
  | 'cs_supervisor'
  | 'cs_analyst'
  | 'credit_manager'
  | 'credit_analyst'
  | 'compliance_officer'
  | 'viewer';

export type UserStatus =
  | 'active'
  | 'inactive'
  | 'locked'
  | 'pending';

export interface UserPermission {
  resource: string;      // e.g., 'custom_fields', 'page_layouts', 'users'
  action: string;        // e.g., 'create', 'read', 'update', 'delete'
  scope: 'all' | 'own' | 'team' | 'none';
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  applications: Application[];  // Which apps user can access
  permissions: UserPermission[];
  profileImage?: string;
  phoneNumber?: string;
  department?: string;
  title?: string;
  manager?: string;  // User ID of manager
  mfaEnabled: boolean;
  lastLoginAt?: string;
  lastLoginIp?: string;
  failedLoginAttempts: number;
  passwordLastChangedAt: string;
  passwordExpiresAt?: string;
  mustChangePassword: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface UserActivityLog {
  id: string;
  userId: string;
  userName: string;
  action: string;         // e.g., 'login', 'logout', 'create_field', 'update_layout'
  resource?: string;      // e.g., 'custom_field', 'page_layout', 'user'
  resourceId?: string;    // ID of the resource affected
  details?: string;       // Additional context
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

export interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  lockedUsers: number;
  pendingUsers: number;
  byRole: Record<UserRole, number>;
  byApplication: Record<Application, number>;
  recentLogins: UserActivityLog[];
  recentActivity: UserActivityLog[];
}

export interface BulkUserOperation {
  operation: 'activate' | 'deactivate' | 'lock' | 'unlock' | 'delete' | 'assign_role';
  userIds: string[];
  newRole?: UserRole;  // Required if operation is 'assign_role'
}

// ===========================
// THEME & SETTINGS TYPES
// ===========================

export type ThemeMode = 'light' | 'dark' | 'auto';

export type ColorScheme =
  | 'blue'      // Default professional blue
  | 'green'     // Success/growth focused
  | 'purple'    // Creative/modern
  | 'red'       // Alert/urgent focused
  | 'orange'    // Warm/friendly
  | 'teal';     // Calm/balanced

export type DateFormat =
  | 'MM/DD/YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY-MM-DD'
  | 'MMM DD, YYYY'
  | 'DD MMM YYYY';

export type TimeFormat = '12h' | '24h';

export type NumberFormat =
  | 'en-US'     // 1,234.56
  | 'en-GB'     // 1,234.56
  | 'de-DE'     // 1.234,56
  | 'fr-FR'     // 1 234,56
  | 'es-ES';    // 1.234,56

export interface ThemeSettings {
  mode: ThemeMode;
  colorScheme: ColorScheme;
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
}

export interface DisplaySettings {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  timezone: string;  // e.g., 'America/New_York', 'Europe/London'
  numberFormat: NumberFormat;
  currencySymbol: string;  // e.g., '$', '€', '£'
  itemsPerPage: number;  // Default pagination size
  showGridLines: boolean;
  defaultView: 'grid' | 'list' | 'kanban';
}

export interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  desktopNotifications: boolean;
  soundEnabled: boolean;
  notifyOnTaskAssignment: boolean;
  notifyOnTaskCompletion: boolean;
  notifyOnMention: boolean;
  notifyOnComment: boolean;
  dailyDigestEnabled: boolean;
  dailyDigestTime: string;  // e.g., '09:00'
}

export interface SecuritySettings {
  sessionTimeout: number;  // in minutes
  mfaEnabled: boolean;
  passwordExpiryDays: number;  // 0 = never expires
  loginNotifications: boolean;
  allowedIpAddresses: string[];  // Empty = all IPs allowed
  requirePasswordChange: boolean;
  lockoutThreshold: number;  // Failed login attempts before lockout
  lockoutDuration: number;  // in minutes
}

export interface SystemSettings {
  language: string;  // e.g., 'en-US', 'es-ES', 'fr-FR'
  autoSaveEnabled: boolean;
  autoSaveInterval: number;  // in seconds
  debugMode: boolean;
  analyticsEnabled: boolean;
  errorReportingEnabled: boolean;
  maintenanceMode: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  backupTime: string;  // e.g., '02:00'
}

export interface ApplicationSettings {
  theme: ThemeSettings;
  display: DisplaySettings;
  notifications: NotificationSettings;
  security: SecuritySettings;
  system: SystemSettings;
  lastUpdated: string;
  updatedBy: string;
}

// ===========================
// MOCK DATA
// ===========================

/**
 * All 57 existing CyberSweep custom fields with Salesforce-compatible __c suffix
 * This data will eventually be replaced by backend API calls
 */
let mockCustomFields: CustomField[] = [
  // PERSONAL INFO SECTION
  {
    id: 'field_001',
    fieldKey: 'first_name__c',
    label: 'First Name',
    type: 'text',
    application: 'cybersweep',
    section: 'personal-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: 'Enter first name',
    validationRules: {
      minLength: 1,
      maxLength: 50
    },
    displayOrder: 1,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_002',
    fieldKey: 'middle_name__c',
    label: 'Middle Name',
    type: 'text',
    application: 'cybersweep',
    section: 'personal-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Enter middle name',
    validationRules: {
      maxLength: 50
    },
    displayOrder: 2,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_003',
    fieldKey: 'last_name__c',
    label: 'Last Name',
    type: 'text',
    application: 'cybersweep',
    section: 'personal-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: 'Enter last name',
    validationRules: {
      minLength: 1,
      maxLength: 50
    },
    displayOrder: 3,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_004',
    fieldKey: 'suffix__c',
    label: 'Suffix',
    type: 'select',
    application: 'cybersweep',
    section: 'personal-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'None', value: '', isDefault: true },
      { label: 'Jr.', value: 'jr' },
      { label: 'Sr.', value: 'sr' },
      { label: 'II', value: 'ii' },
      { label: 'III', value: 'iii' },
      { label: 'IV', value: 'iv' }
    ],
    displayOrder: 4,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_005',
    fieldKey: 'date_of_birth__c',
    label: 'Date of Birth',
    type: 'date',
    application: 'cybersweep',
    section: 'personal-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    helpText: 'Must be 18 years or older',
    displayOrder: 5,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_006',
    fieldKey: 'gender__c',
    label: 'Gender',
    type: 'select',
    application: 'cybersweep',
    section: 'personal-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'Male', value: 'male' },
      { label: 'Female', value: 'female' },
      { label: 'Non-binary', value: 'non-binary' },
      { label: 'Prefer not to say', value: 'prefer-not-to-say' }
    ],
    displayOrder: 6,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // CONTACT INFO SECTION
  {
    id: 'field_007',
    fieldKey: 'email__c',
    label: 'Email Address',
    type: 'email',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: 'email@example.com',
    validationRules: {
      pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$'
    },
    displayOrder: 7,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_008',
    fieldKey: 'phone__c',
    label: 'Phone Number',
    type: 'phone',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: '(555) 123-4567',
    validationRules: {
      pattern: '^\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$'
    },
    displayOrder: 8,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_009',
    fieldKey: 'alternate_phone__c',
    label: 'Alternate Phone',
    type: 'phone',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '(555) 123-4567',
    displayOrder: 9,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_010',
    fieldKey: 'street_address__c',
    label: 'Street Address',
    type: 'text',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: '123 Main Street',
    validationRules: {
      maxLength: 100
    },
    displayOrder: 10,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_011',
    fieldKey: 'city__c',
    label: 'City',
    type: 'text',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: 'Los Angeles',
    validationRules: {
      maxLength: 50
    },
    displayOrder: 11,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_012',
    fieldKey: 'state__c',
    label: 'State',
    type: 'select',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'Alabama', value: 'AL' },
      { label: 'Alaska', value: 'AK' },
      { label: 'Arizona', value: 'AZ' },
      { label: 'California', value: 'CA' },
      { label: 'Florida', value: 'FL' },
      { label: 'New York', value: 'NY' },
      { label: 'Texas', value: 'TX' }
      // ... would include all 50 states
    ],
    displayOrder: 12,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_013',
    fieldKey: 'zip_code__c',
    label: 'ZIP Code',
    type: 'text',
    application: 'cybersweep',
    section: 'contact-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: '90210',
    validationRules: {
      pattern: '^\\d{5}(-\\d{4})?$',
      minLength: 5,
      maxLength: 10
    },
    displayOrder: 13,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // IDENTITY INFO SECTION
  {
    id: 'field_014',
    fieldKey: 'ssn__c',
    label: 'Social Security Number',
    type: 'text',
    application: 'cybersweep',
    section: 'identity-info',
    isRequired: true,
    isActive: true,
    isSystem: true,
    placeholder: 'XXX-XX-XXXX',
    helpText: 'Required for identity verification',
    validationRules: {
      pattern: '^\\d{3}-?\\d{2}-?\\d{4}$'
    },
    displayOrder: 14,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_015',
    fieldKey: 'drivers_license__c',
    label: 'Driver\'s License',
    type: 'text',
    application: 'cybersweep',
    section: 'identity-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'DL123456',
    validationRules: {
      maxLength: 20
    },
    displayOrder: 15,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_016',
    fieldKey: 'license_state__c',
    label: 'License State',
    type: 'select',
    application: 'cybersweep',
    section: 'identity-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'California', value: 'CA' },
      { label: 'New York', value: 'NY' },
      { label: 'Texas', value: 'TX' }
      // ... would include all 50 states
    ],
    displayOrder: 16,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_017',
    fieldKey: 'passport_number__c',
    label: 'Passport Number',
    type: 'text',
    application: 'cybersweep',
    section: 'identity-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'A12345678',
    validationRules: {
      maxLength: 20
    },
    displayOrder: 17,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // FINANCIAL INFO SECTION
  {
    id: 'field_018',
    fieldKey: 'employment_status__c',
    label: 'Employment Status',
    type: 'select',
    application: 'cybersweep',
    section: 'financial-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'Employed Full-Time', value: 'full-time' },
      { label: 'Employed Part-Time', value: 'part-time' },
      { label: 'Self-Employed', value: 'self-employed' },
      { label: 'Unemployed', value: 'unemployed' },
      { label: 'Retired', value: 'retired' },
      { label: 'Student', value: 'student' }
    ],
    displayOrder: 18,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_019',
    fieldKey: 'employer_name__c',
    label: 'Employer Name',
    type: 'text',
    application: 'cybersweep',
    section: 'financial-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Company Name',
    validationRules: {
      maxLength: 100
    },
    displayOrder: 19,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_020',
    fieldKey: 'annual_income__c',
    label: 'Annual Income',
    type: 'currency',
    application: 'cybersweep',
    section: 'financial-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '50000',
    helpText: 'Enter annual gross income',
    validationRules: {
      minValue: 0,
      maxValue: 10000000
    },
    displayOrder: 20,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_021',
    fieldKey: 'bank_name__c',
    label: 'Bank Name',
    type: 'text',
    application: 'cybersweep',
    section: 'financial-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Chase Bank',
    validationRules: {
      maxLength: 100
    },
    displayOrder: 21,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_022',
    fieldKey: 'account_number__c',
    label: 'Account Number',
    type: 'text',
    application: 'cybersweep',
    section: 'financial-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'XXXX-XXXX-XXXX-1234',
    helpText: 'Last 4 digits only',
    validationRules: {
      maxLength: 20
    },
    displayOrder: 22,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_023',
    fieldKey: 'routing_number__c',
    label: 'Routing Number',
    type: 'text',
    application: 'cybersweep',
    section: 'financial-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '123456789',
    validationRules: {
      pattern: '^\\d{9}$'
    },
    displayOrder: 23,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // CASE DETAILS SECTION
  {
    id: 'field_024',
    fieldKey: 'case_status__c',
    label: 'Case Status',
    type: 'select',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: true,
    isActive: true,
    isSystem: true,
    defaultValue: 'new',
    options: [
      { label: 'New', value: 'new', isDefault: true },
      { label: 'In Progress', value: 'in-progress' },
      { label: 'Pending Review', value: 'pending-review' },
      { label: 'On Hold', value: 'on-hold' },
      { label: 'Completed', value: 'completed' },
      { label: 'Cancelled', value: 'cancelled' }
    ],
    displayOrder: 24,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_025',
    fieldKey: 'priority__c',
    label: 'Priority',
    type: 'select',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: true,
    isActive: true,
    isSystem: true,
    defaultValue: 'medium',
    options: [
      { label: 'Low', value: 'low' },
      { label: 'Medium', value: 'medium', isDefault: true },
      { label: 'High', value: 'high' },
      { label: 'Urgent', value: 'urgent' }
    ],
    displayOrder: 25,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_026',
    fieldKey: 'assigned_to__c',
    label: 'Assigned To',
    type: 'select',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Unassigned', value: '', isDefault: true },
      { label: 'John Doe', value: 'user_001' },
      { label: 'Jane Smith', value: 'user_002' },
      { label: 'Mike Johnson', value: 'user_003' }
    ],
    displayOrder: 26,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_027',
    fieldKey: 'case_type__c',
    label: 'Case Type',
    type: 'select',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: true,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Credit Repair', value: 'credit-repair' },
      { label: 'Identity Theft', value: 'identity-theft' },
      { label: 'Credit Development', value: 'credit-development' },
      { label: 'Credit Monitoring', value: 'credit-monitoring' },
      { label: 'Dispute Resolution', value: 'dispute-resolution' }
    ],
    displayOrder: 27,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_028',
    fieldKey: 'intake_date__c',
    label: 'Intake Date',
    type: 'date',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: true,
    isActive: true,
    isSystem: true,
    defaultValue: new Date().toISOString().split('T')[0],
    displayOrder: 28,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_029',
    fieldKey: 'target_completion_date__c',
    label: 'Target Completion Date',
    type: 'date',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Expected case completion date',
    displayOrder: 29,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_030',
    fieldKey: 'case_notes__c',
    label: 'Case Notes',
    type: 'textarea',
    application: 'cybersweep',
    section: 'case-details',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Add case notes...',
    validationRules: {
      maxLength: 5000
    },
    displayOrder: 30,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // DISPUTE INFO SECTION
  {
    id: 'field_031',
    fieldKey: 'dispute_reason__c',
    label: 'Dispute Reason',
    type: 'select',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'Not Mine', value: 'not-mine' },
      { label: 'Incorrect Amount', value: 'incorrect-amount' },
      { label: 'Paid in Full', value: 'paid-in-full' },
      { label: 'Duplicate Account', value: 'duplicate' },
      { label: 'Identity Theft', value: 'identity-theft' },
      { label: 'Other', value: 'other' }
    ],
    displayOrder: 31,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_032',
    fieldKey: 'dispute_date__c',
    label: 'Dispute Date',
    type: 'date',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    displayOrder: 32,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_033',
    fieldKey: 'dispute_status__c',
    label: 'Dispute Status',
    type: 'select',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Not Started', value: 'not-started', isDefault: true },
      { label: 'Filed', value: 'filed' },
      { label: 'Under Investigation', value: 'under-investigation' },
      { label: 'Resolved', value: 'resolved' },
      { label: 'Rejected', value: 'rejected' }
    ],
    displayOrder: 33,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_034',
    fieldKey: 'account_name__c',
    label: 'Account Name',
    type: 'text',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Credit Card Company',
    validationRules: {
      maxLength: 100
    },
    displayOrder: 34,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_035',
    fieldKey: 'account_type__c',
    label: 'Account Type',
    type: 'select',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'Credit Card', value: 'credit-card' },
      { label: 'Auto Loan', value: 'auto-loan' },
      { label: 'Mortgage', value: 'mortgage' },
      { label: 'Student Loan', value: 'student-loan' },
      { label: 'Personal Loan', value: 'personal-loan' },
      { label: 'Collection', value: 'collection' },
      { label: 'Medical', value: 'medical' }
    ],
    displayOrder: 35,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_036',
    fieldKey: 'original_balance__c',
    label: 'Original Balance',
    type: 'currency',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '1000.00',
    validationRules: {
      minValue: 0
    },
    displayOrder: 36,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_037',
    fieldKey: 'current_balance__c',
    label: 'Current Balance',
    type: 'currency',
    application: 'cybersweep',
    section: 'dispute-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '850.00',
    validationRules: {
      minValue: 0
    },
    displayOrder: 37,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // BUREAU INFO SECTION
  {
    id: 'field_038',
    fieldKey: 'experian_score__c',
    label: 'Experian Score',
    type: 'number',
    application: 'cybersweep',
    section: 'bureau-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '650',
    helpText: 'Current Experian credit score',
    validationRules: {
      minValue: 300,
      maxValue: 850
    },
    displayOrder: 38,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_039',
    fieldKey: 'equifax_score__c',
    label: 'Equifax Score',
    type: 'number',
    application: 'cybersweep',
    section: 'bureau-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '650',
    helpText: 'Current Equifax credit score',
    validationRules: {
      minValue: 300,
      maxValue: 850
    },
    displayOrder: 39,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_040',
    fieldKey: 'transunion_score__c',
    label: 'TransUnion Score',
    type: 'number',
    application: 'cybersweep',
    section: 'bureau-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '650',
    helpText: 'Current TransUnion credit score',
    validationRules: {
      minValue: 300,
      maxValue: 850
    },
    displayOrder: 40,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_041',
    fieldKey: 'target_score__c',
    label: 'Target Score',
    type: 'number',
    application: 'cybersweep',
    section: 'bureau-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: '750',
    helpText: 'Desired credit score goal',
    validationRules: {
      minValue: 300,
      maxValue: 850
    },
    displayOrder: 41,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_042',
    fieldKey: 'last_pull_date__c',
    label: 'Last Pull Date',
    type: 'date',
    application: 'cybersweep',
    section: 'bureau-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Date of last credit report pull',
    displayOrder: 42,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_043',
    fieldKey: 'bureaus_contacted__c',
    label: 'Bureaus Contacted',
    type: 'multiselect',
    application: 'cybersweep',
    section: 'bureau-info',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Experian', value: 'experian' },
      { label: 'Equifax', value: 'equifax' },
      { label: 'TransUnion', value: 'transunion' }
    ],
    displayOrder: 43,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // DOCUMENTS SECTION
  {
    id: 'field_044',
    fieldKey: 'id_verification_doc__c',
    label: 'ID Verification Document',
    type: 'text',
    application: 'cybersweep',
    section: 'documents',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Reference to uploaded ID document',
    displayOrder: 44,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_045',
    fieldKey: 'proof_of_address__c',
    label: 'Proof of Address',
    type: 'text',
    application: 'cybersweep',
    section: 'documents',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Reference to uploaded address proof document',
    displayOrder: 45,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_046',
    fieldKey: 'credit_report_doc__c',
    label: 'Credit Report Document',
    type: 'text',
    application: 'cybersweep',
    section: 'documents',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Reference to uploaded credit report',
    displayOrder: 46,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_047',
    fieldKey: 'police_report__c',
    label: 'Police Report',
    type: 'text',
    application: 'cybersweep',
    section: 'documents',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Reference to uploaded police report (for identity theft)',
    displayOrder: 47,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_048',
    fieldKey: 'ftc_affidavit__c',
    label: 'FTC Affidavit',
    type: 'text',
    application: 'cybersweep',
    section: 'documents',
    isRequired: false,
    isActive: true,
    isSystem: true,
    helpText: 'Reference to uploaded FTC Identity Theft Affidavit',
    displayOrder: 48,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },

  // METADATA SECTION
  {
    id: 'field_049',
    fieldKey: 'lead_source__c',
    label: 'Lead Source',
    type: 'select',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Select...', value: '', isDefault: true },
      { label: 'Website', value: 'website' },
      { label: 'Referral', value: 'referral' },
      { label: 'Phone Call', value: 'phone' },
      { label: 'Email', value: 'email' },
      { label: 'Social Media', value: 'social' },
      { label: 'Advertisement', value: 'ad' },
      { label: 'Walk-in', value: 'walk-in' }
    ],
    displayOrder: 49,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_050',
    fieldKey: 'referral_name__c',
    label: 'Referral Name',
    type: 'text',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Name of person who referred',
    validationRules: {
      maxLength: 100
    },
    displayOrder: 50,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_051',
    fieldKey: 'campaign_id__c',
    label: 'Campaign ID',
    type: 'text',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    placeholder: 'Marketing campaign identifier',
    validationRules: {
      maxLength: 50
    },
    displayOrder: 51,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_052',
    fieldKey: 'consent_marketing__c',
    label: 'Marketing Consent',
    type: 'boolean',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    defaultValue: false,
    helpText: 'Customer consents to marketing communications',
    displayOrder: 52,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_053',
    fieldKey: 'consent_data_sharing__c',
    label: 'Data Sharing Consent',
    type: 'boolean',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    defaultValue: false,
    helpText: 'Customer consents to data sharing with partners',
    displayOrder: 53,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_054',
    fieldKey: 'preferred_contact_method__c',
    label: 'Preferred Contact Method',
    type: 'select',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Email', value: 'email', isDefault: true },
      { label: 'Phone', value: 'phone' },
      { label: 'SMS', value: 'sms' },
      { label: 'Mail', value: 'mail' }
    ],
    displayOrder: 54,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_055',
    fieldKey: 'preferred_contact_time__c',
    label: 'Preferred Contact Time',
    type: 'select',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    options: [
      { label: 'Morning (8am-12pm)', value: 'morning' },
      { label: 'Afternoon (12pm-5pm)', value: 'afternoon', isDefault: true },
      { label: 'Evening (5pm-8pm)', value: 'evening' }
    ],
    displayOrder: 55,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_056',
    fieldKey: 'timezone__c',
    label: 'Timezone',
    type: 'select',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    defaultValue: 'America/Los_Angeles',
    options: [
      { label: 'Eastern (ET)', value: 'America/New_York' },
      { label: 'Central (CT)', value: 'America/Chicago' },
      { label: 'Mountain (MT)', value: 'America/Denver' },
      { label: 'Pacific (PT)', value: 'America/Los_Angeles', isDefault: true },
      { label: 'Alaska (AKT)', value: 'America/Anchorage' },
      { label: 'Hawaii (HST)', value: 'Pacific/Honolulu' }
    ],
    displayOrder: 56,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'field_057',
    fieldKey: 'language_preference__c',
    label: 'Language Preference',
    type: 'select',
    application: 'cybersweep',
    section: 'metadata',
    isRequired: false,
    isActive: true,
    isSystem: true,
    defaultValue: 'en',
    options: [
      { label: 'English', value: 'en', isDefault: true },
      { label: 'Spanish', value: 'es' },
      { label: 'French', value: 'fr' },
      { label: 'Mandarin', value: 'zh' }
    ],
    displayOrder: 57,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system'
  }
];

// ============================================
// MOCK DATA - PAGE LAYOUT TEMPLATES
// ============================================

const MOCK_PAGE_LAYOUT_TEMPLATES: PageLayoutTemplate[] = [
  {
    id: 'template_001',
    name: 'CyberSweep Default Layout',
    description: 'Standard layout for CyberSweep client details',
    application: 'cybersweep',
    isPublic: true,
    isDefault: true,
    version: 1,
    sections: [
      {
        id: 'section_001',
        name: 'Basic Information',
        description: 'Core client details',
        page: 'cybersweep-client-detail',
        tab: 'overview',
        columns: 2,
        order: 1,
        collapsible: false,
        defaultCollapsed: false,
        visible: true,
        fields: [
          {
            fieldId: 'field_001',
            label: 'Client Name',
            fieldKey: 'clientName__c',
            type: 'text',
            required: true,
            order: 1,
            width: 'half'
          },
          {
            fieldId: 'field_002',
            label: 'Contact Name',
            fieldKey: 'contactName__c',
            type: 'text',
            required: true,
            order: 2,
            width: 'half'
          },
          {
            fieldId: 'field_003',
            label: 'Email Address',
            fieldKey: 'emailAddress__c',
            type: 'email',
            required: true,
            order: 3,
            width: 'half',
            piiConfig: {
              encrypted: true,
              encryptionLevel: 'standard',
              masked: false,
              accessRoles: ['system_admin', 'cs_supervisor']
            }
          },
          {
            fieldId: 'field_004',
            label: 'Phone',
            fieldKey: 'phone__c',
            type: 'phone',
            required: false,
            order: 4,
            width: 'half',
            piiConfig: {
              encrypted: true,
              encryptionLevel: 'standard',
              masked: true,
              maskingPattern: '(***) ***-####',
              accessRoles: ['system_admin', 'cs_supervisor']
            }
          }
        ],
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
        createdBy: 'system'
      },
      {
        id: 'section_002',
        name: 'Service Details',
        description: 'Subscription and service information',
        page: 'cybersweep-client-detail',
        tab: 'overview',
        columns: 2,
        order: 2,
        collapsible: true,
        defaultCollapsed: false,
        visible: true,
        fields: [
          {
            fieldId: 'field_005',
            label: 'Service Type',
            fieldKey: 'serviceType__c',
            type: 'select',
            required: true,
            order: 1,
            width: 'half'
          },
          {
            fieldId: 'field_006',
            label: 'Enrollment Date',
            fieldKey: 'enrollmentDate__c',
            type: 'date',
            required: true,
            order: 2,
            width: 'half'
          },
          {
            fieldId: 'field_007',
            label: 'Current Status',
            fieldKey: 'currentStatus__c',
            type: 'select',
            required: true,
            order: 3,
            width: 'half'
          },
          {
            fieldId: 'field_008',
            label: 'Workflow Stage',
            fieldKey: 'workflowStage__c',
            type: 'select',
            required: true,
            order: 4,
            width: 'half'
          }
        ],
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
        createdBy: 'system'
      }
    ],
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'template_002',
    name: 'Credit Repair Default Layout',
    description: 'Standard layout for Credit Repair cases',
    application: 'credit-repair',
    isPublic: true,
    isDefault: true,
    version: 1,
    sections: [
      {
        id: 'section_003',
        name: 'Client Overview',
        page: 'credit-case-detail',
        tab: 'overview',
        columns: 2,
        order: 1,
        collapsible: false,
        defaultCollapsed: false,
        visible: true,
        fields: [],
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
        createdBy: 'system'
      }
    ],
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
    createdBy: 'system'
  }
];

// ============================================
// MOCK DATA - LIST VIEW TEMPLATES
// ============================================

let MOCK_LIST_VIEW_TEMPLATES: ListViewTemplate[] = [
  {
    id: 'listview_001',
    name: 'CyberSweep Clients - Default',
    description: 'Default list view for CyberSweep client records',
    application: 'cybersweep',
    entityType: 'clients',
    isPublic: true,
    isDefault: true,
    columns: [
      {
        id: 'col_001',
        fieldKey: 'first_name__c',
        label: 'First Name',
        dataType: 'text',
        width: 150,
        minWidth: 100,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 1,
        alignment: 'left'
      },
      {
        id: 'col_002',
        fieldKey: 'last_name__c',
        label: 'Last Name',
        dataType: 'text',
        width: 150,
        minWidth: 100,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 2,
        alignment: 'left'
      },
      {
        id: 'col_003',
        fieldKey: 'email__c',
        label: 'Email',
        dataType: 'text',
        width: 200,
        minWidth: 150,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 3,
        alignment: 'left'
      },
      {
        id: 'col_004',
        fieldKey: 'phone__c',
        label: 'Phone',
        dataType: 'text',
        width: 140,
        minWidth: 120,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 4,
        alignment: 'left'
      },
      {
        id: 'col_005',
        fieldKey: 'case_status__c',
        label: 'Status',
        dataType: 'enum',
        width: 120,
        minWidth: 100,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 5,
        alignment: 'center'
      },
      {
        id: 'col_006',
        fieldKey: 'intake_date__c',
        label: 'Intake Date',
        dataType: 'date',
        width: 130,
        minWidth: 110,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 6,
        alignment: 'left',
        format: 'MM/DD/YYYY'
      },
      {
        id: 'col_007',
        fieldKey: 'assigned_to__c',
        label: 'Assigned To',
        dataType: 'text',
        width: 150,
        minWidth: 120,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 7,
        alignment: 'left'
      }
    ],
    defaultSort: [
      {
        fieldKey: 'last_name__c',
        direction: 'asc',
        priority: 1
      }
    ],
    rowsPerPage: 25,
    rowsPerPageOptions: [10, 25, 50, 100],
    enableExport: true,
    exportFormats: ['csv', 'xlsx', 'pdf'],
    enableColumnReorder: true,
    enableColumnResize: true,
    enableQuickFilters: true,
    createdAt: '2025-01-15T00:00:00Z',
    updatedAt: '2025-01-15T00:00:00Z',
    createdBy: 'system'
  },
  {
    id: 'listview_002',
    name: 'Credit Cases - Default',
    description: 'Default list view for credit repair cases',
    application: 'credit-repair',
    entityType: 'cases',
    isPublic: true,
    isDefault: true,
    columns: [
      {
        id: 'col_101',
        fieldKey: 'case_number__c',
        label: 'Case #',
        dataType: 'text',
        width: 120,
        minWidth: 100,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 1,
        alignment: 'left'
      },
      {
        id: 'col_102',
        fieldKey: 'client_name__c',
        label: 'Client Name',
        dataType: 'text',
        width: 200,
        minWidth: 150,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 2,
        alignment: 'left'
      },
      {
        id: 'col_103',
        fieldKey: 'case_type__c',
        label: 'Case Type',
        dataType: 'enum',
        width: 160,
        minWidth: 120,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 3,
        alignment: 'left'
      },
      {
        id: 'col_104',
        fieldKey: 'priority__c',
        label: 'Priority',
        dataType: 'enum',
        width: 100,
        minWidth: 80,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 4,
        alignment: 'center'
      },
      {
        id: 'col_105',
        fieldKey: 'target_score__c',
        label: 'Target Score',
        dataType: 'number',
        width: 120,
        minWidth: 100,
        sortable: true,
        filterable: true,
        resizable: true,
        visible: true,
        order: 5,
        alignment: 'right'
      }
    ],
    defaultSort: [
      {
        fieldKey: 'priority__c',
        direction: 'desc',
        priority: 1
      },
      {
        fieldKey: 'intake_date__c',
        direction: 'desc',
        priority: 2
      }
    ],
    defaultFilters: [
      {
        fieldKey: 'case_status__c',
        operator: 'in',
        value: ['new', 'in-progress', 'pending-review'],
        logicalOperator: 'AND'
      }
    ],
    rowsPerPage: 50,
    rowsPerPageOptions: [25, 50, 100, 200],
    enableExport: true,
    exportFormats: ['csv', 'xlsx'],
    enableColumnReorder: true,
    enableColumnResize: true,
    enableQuickFilters: true,
    createdAt: '2025-01-15T00:00:00Z',
    updatedAt: '2025-01-15T00:00:00Z',
    createdBy: 'system'
  }
];

// ============================================
// MOCK DATA - USER MANAGEMENT
// ============================================

/**
 * Mock user data with different roles, statuses, and permissions
 * This will be replaced by backend API calls
 */
let MOCK_USERS: User[] = [
  {
    id: 'user_001',
    username: 'system_admin',
    email: 'admin@regal.com',
    firstName: 'System',
    lastName: 'Administrator',
    role: 'system_admin',
    status: 'active',
    applications: ['cybersweep', 'credit-repair', 'id-theft', 'credit-block'],
    permissions: [
      { resource: '*', action: '*', scope: 'all' }  // Full access
    ],
    phoneNumber: '+1 (555) 000-0001',
    department: 'IT',
    title: 'System Administrator',
    mfaEnabled: true,
    lastLoginAt: '2025-01-20T08:30:00Z',
    lastLoginIp: '192.168.1.100',
    failedLoginAttempts: 0,
    passwordLastChangedAt: '2025-01-01T00:00:00Z',
    mustChangePassword: false,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2025-01-20T08:30:00Z',
    createdBy: 'system'
  },
  {
    id: 'user_002',
    username: 'jdoe',
    email: 'john.doe@regal.com',
    firstName: 'John',
    lastName: 'Doe',
    role: 'cs_supervisor',
    status: 'active',
    applications: ['cybersweep', 'credit-repair'],
    permissions: [
      { resource: 'custom_fields', action: 'create', scope: 'all' },
      { resource: 'custom_fields', action: 'read', scope: 'all' },
      { resource: 'custom_fields', action: 'update', scope: 'all' },
      { resource: 'custom_fields', action: 'delete', scope: 'team' },
      { resource: 'page_layouts', action: 'read', scope: 'all' },
      { resource: 'page_layouts', action: 'update', scope: 'team' },
      { resource: 'users', action: 'read', scope: 'team' }
    ],
    phoneNumber: '+1 (555) 123-4567',
    department: 'Customer Success',
    title: 'CS Supervisor',
    manager: 'user_001',
    mfaEnabled: true,
    lastLoginAt: '2025-01-21T09:15:00Z',
    lastLoginIp: '192.168.1.105',
    failedLoginAttempts: 0,
    passwordLastChangedAt: '2024-12-01T00:00:00Z',
    passwordExpiresAt: '2025-06-01T00:00:00Z',
    mustChangePassword: false,
    createdAt: '2024-02-15T00:00:00Z',
    updatedAt: '2025-01-21T09:15:00Z',
    createdBy: 'user_001'
  },
  {
    id: 'user_003',
    username: 'asmith',
    email: 'alice.smith@regal.com',
    firstName: 'Alice',
    lastName: 'Smith',
    role: 'cs_analyst',
    status: 'active',
    applications: ['cybersweep'],
    permissions: [
      { resource: 'custom_fields', action: 'read', scope: 'all' },
      { resource: 'custom_fields', action: 'update', scope: 'own' },
      { resource: 'page_layouts', action: 'read', scope: 'all' },
      { resource: 'clients', action: 'read', scope: 'team' },
      { resource: 'clients', action: 'update', scope: 'own' }
    ],
    phoneNumber: '+1 (555) 234-5678',
    department: 'Customer Success',
    title: 'CS Analyst',
    manager: 'user_002',
    mfaEnabled: false,
    lastLoginAt: '2025-01-21T10:00:00Z',
    lastLoginIp: '192.168.1.110',
    failedLoginAttempts: 0,
    passwordLastChangedAt: '2024-11-15T00:00:00Z',
    passwordExpiresAt: '2025-05-15T00:00:00Z',
    mustChangePassword: false,
    createdAt: '2024-03-10T00:00:00Z',
    updatedAt: '2025-01-21T10:00:00Z',
    createdBy: 'user_002'
  },
  {
    id: 'user_004',
    username: 'bjones',
    email: 'bob.jones@regal.com',
    firstName: 'Bob',
    lastName: 'Jones',
    role: 'credit_manager',
    status: 'active',
    applications: ['credit-repair', 'id-theft'],
    permissions: [
      { resource: 'custom_fields', action: 'create', scope: 'all' },
      { resource: 'custom_fields', action: 'read', scope: 'all' },
      { resource: 'custom_fields', action: 'update', scope: 'all' },
      { resource: 'page_layouts', action: 'read', scope: 'all' },
      { resource: 'page_layouts', action: 'update', scope: 'team' },
      { resource: 'cases', action: '*', scope: 'team' }
    ],
    phoneNumber: '+1 (555) 345-6789',
    department: 'Credit Services',
    title: 'Credit Manager',
    manager: 'user_001',
    mfaEnabled: true,
    lastLoginAt: '2025-01-20T14:30:00Z',
    lastLoginIp: '192.168.1.115',
    failedLoginAttempts: 0,
    passwordLastChangedAt: '2024-12-20T00:00:00Z',
    passwordExpiresAt: '2025-06-20T00:00:00Z',
    mustChangePassword: false,
    createdAt: '2024-01-20T00:00:00Z',
    updatedAt: '2025-01-20T14:30:00Z',
    createdBy: 'user_001'
  },
  {
    id: 'user_005',
    username: 'cwilliams',
    email: 'carol.williams@regal.com',
    firstName: 'Carol',
    lastName: 'Williams',
    role: 'viewer',
    status: 'inactive',
    applications: ['cybersweep'],
    permissions: [
      { resource: 'custom_fields', action: 'read', scope: 'all' },
      { resource: 'page_layouts', action: 'read', scope: 'all' },
      { resource: 'clients', action: 'read', scope: 'all' }
    ],
    phoneNumber: '+1 (555) 456-7890',
    department: 'Compliance',
    title: 'Compliance Viewer',
    manager: 'user_001',
    mfaEnabled: false,
    lastLoginAt: '2024-12-15T11:00:00Z',
    lastLoginIp: '192.168.1.120',
    failedLoginAttempts: 0,
    passwordLastChangedAt: '2024-10-01T00:00:00Z',
    passwordExpiresAt: '2025-04-01T00:00:00Z',
    mustChangePassword: false,
    createdAt: '2024-05-01T00:00:00Z',
    updatedAt: '2024-12-20T00:00:00Z',
    createdBy: 'user_001',
    updatedBy: 'user_001'
  },
  {
    id: 'user_006',
    username: 'dmiller',
    email: 'david.miller@regal.com',
    firstName: 'David',
    lastName: 'Miller',
    role: 'cs_analyst',
    status: 'locked',
    applications: ['cybersweep'],
    permissions: [
      { resource: 'custom_fields', action: 'read', scope: 'all' },
      { resource: 'page_layouts', action: 'read', scope: 'all' },
      { resource: 'clients', action: 'read', scope: 'team' }
    ],
    phoneNumber: '+1 (555) 567-8901',
    department: 'Customer Success',
    title: 'CS Analyst',
    manager: 'user_002',
    mfaEnabled: false,
    lastLoginAt: '2025-01-18T16:45:00Z',
    lastLoginIp: '192.168.1.125',
    failedLoginAttempts: 5,
    passwordLastChangedAt: '2024-09-01T00:00:00Z',
    passwordExpiresAt: '2025-03-01T00:00:00Z',
    mustChangePassword: true,
    createdAt: '2024-04-15T00:00:00Z',
    updatedAt: '2025-01-18T16:50:00Z',
    createdBy: 'user_002',
    updatedBy: 'system'
  }
];

/**
 * Mock user activity logs
 */
let MOCK_USER_ACTIVITY: UserActivityLog[] = [
  {
    id: 'activity_001',
    userId: 'user_002',
    userName: 'John Doe',
    action: 'login',
    ipAddress: '192.168.1.105',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    timestamp: '2025-01-21T09:15:00Z'
  },
  {
    id: 'activity_002',
    userId: 'user_002',
    userName: 'John Doe',
    action: 'create_field',
    resource: 'custom_field',
    resourceId: 'field_058',
    details: 'Created new custom field: Middle Name',
    ipAddress: '192.168.1.105',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    timestamp: '2025-01-21T09:30:00Z'
  },
  {
    id: 'activity_003',
    userId: 'user_003',
    userName: 'Alice Smith',
    action: 'update_client',
    resource: 'client',
    resourceId: 'client_001',
    details: 'Updated client phone number',
    ipAddress: '192.168.1.110',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
    timestamp: '2025-01-21T10:15:00Z'
  }
];

// ============================================
// MOCK DATA - APPLICATION SETTINGS
// ============================================

let MOCK_SETTINGS: ApplicationSettings = {
  theme: {
    mode: 'auto',
    colorScheme: 'blue',
    fontSize: 'medium',
    compactMode: false,
    highContrast: false,
    reducedMotion: false
  },
  display: {
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    timezone: 'America/New_York',
    numberFormat: 'en-US',
    currencySymbol: '$',
    itemsPerPage: 25,
    showGridLines: true,
    defaultView: 'list'
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    desktopNotifications: false,
    soundEnabled: true,
    notifyOnTaskAssignment: true,
    notifyOnTaskCompletion: true,
    notifyOnMention: true,
    notifyOnComment: false,
    dailyDigestEnabled: false,
    dailyDigestTime: '09:00'
  },
  security: {
    sessionTimeout: 30,
    mfaEnabled: false,
    passwordExpiryDays: 90,
    loginNotifications: true,
    allowedIpAddresses: [],
    requirePasswordChange: false,
    lockoutThreshold: 5,
    lockoutDuration: 15
  },
  system: {
    language: 'en-US',
    autoSaveEnabled: true,
    autoSaveInterval: 30,
    debugMode: false,
    analyticsEnabled: true,
    errorReportingEnabled: true,
    maintenanceMode: false,
    backupFrequency: 'daily',
    backupTime: '02:00'
  },
  lastUpdated: new Date().toISOString(),
  updatedBy: 'System'
};

// ============================================
// MOCK DATA - LAYOUT SECTIONS (UI-2 PHASE 5)
// ============================================

const MOCK_LAYOUT_SECTIONS: LayoutSection[] = [
  {
    id: 'section_001',
    layoutId: 'layout_001',
    name: 'basic_info',
    label: 'Basic Information',
    order: 1,
    columns: 2,
    collapsible: false,
    collapsed: false,
    fields: [
      {
        id: 'field_001',
        fieldId: 'customfield_001',
        fieldName: 'clientName__c',
        fieldLabel: 'Client Name',
        fieldType: 'text',
        order: 1,
        column: 1,
        required: true,
        readonly: false,
        width: 'full'
      },
      {
        id: 'field_002',
        fieldId: 'customfield_002',
        fieldName: 'emailAddress__c',
        fieldLabel: 'Email Address',
        fieldType: 'email',
        order: 2,
        column: 1,
        required: true,
        readonly: false,
        width: 'half'
      },
      {
        id: 'field_003',
        fieldId: 'customfield_003',
        fieldName: 'phoneNumber__c',
        fieldLabel: 'Phone Number',
        fieldType: 'phone',
        order: 3,
        column: 2,
        required: false,
        readonly: false,
        width: 'half'
      }
    ]
  },
  {
    id: 'section_002',
    layoutId: 'layout_001',
    name: 'contact_info',
    label: 'Contact Information',
    order: 2,
    columns: 2,
    collapsible: true,
    collapsed: false,
    fields: [
      {
        id: 'field_004',
        fieldId: 'customfield_004',
        fieldName: 'mailingAddress__c',
        fieldLabel: 'Mailing Address',
        fieldType: 'address',
        order: 1,
        column: 1,
        required: false,
        readonly: false,
        width: 'full'
      }
    ]
  }
];

// ============================================
// MOCK DATA - VISIBILITY RULES (UI-2 PHASE 6)
// ============================================

const MOCK_VISIBILITY_RULES: VisibilityRule[] = [
  {
    id: 'rule_001',
    layoutId: 'layout_001',
    name: 'Hide SSN for Non-Compliance Users',
    description: 'Only compliance officers can see SSN field',
    enabled: true,
    priority: 1,
    logic: 'AND',
    conditions: [
      {
        id: 'cond_001',
        fieldName: 'userRole',
        fieldLabel: 'User Role',
        operator: 'not_equals',
        value: 'compliance_officer'
      }
    ],
    action: 'hide',
    target: 'field',
    targetId: 'field_ssn',
    targetName: 'Social Security Number',
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  },
  {
    id: 'rule_002',
    layoutId: 'layout_001',
    name: 'Show Billing Address Section',
    description: 'Show billing address when different from mailing',
    enabled: true,
    priority: 2,
    logic: 'AND',
    conditions: [
      {
        id: 'cond_002',
        fieldName: 'sameAsMailingAddress__c',
        fieldLabel: 'Same as Mailing Address',
        operator: 'equals',
        value: false
      }
    ],
    action: 'show',
    target: 'section',
    targetId: 'section_billing',
    targetName: 'Billing Address',
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  },
  {
    id: 'rule_003',
    layoutId: 'layout_001',
    name: 'Require Email for Active Clients',
    description: 'Email is required when client status is Active',
    enabled: true,
    priority: 3,
    logic: 'AND',
    conditions: [
      {
        id: 'cond_003',
        fieldName: 'clientStatus__c',
        fieldLabel: 'Client Status',
        operator: 'equals',
        value: 'Active'
      }
    ],
    action: 'require',
    target: 'field',
    targetId: 'field_email',
    targetName: 'Email Address',
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  },
  {
    id: 'rule_004',
    layoutId: 'layout_001',
    name: 'Hide Credit Info for Closed Cases',
    description: 'Hide credit information section when case is closed',
    enabled: true,
    priority: 4,
    logic: 'OR',
    conditions: [
      {
        id: 'cond_004a',
        fieldName: 'caseStatus__c',
        fieldLabel: 'Case Status',
        operator: 'equals',
        value: 'Closed'
      },
      {
        id: 'cond_004b',
        fieldName: 'caseStatus__c',
        fieldLabel: 'Case Status',
        operator: 'equals',
        value: 'Cancelled'
      }
    ],
    action: 'hide',
    target: 'section',
    targetId: 'section_credit',
    targetName: 'Credit Information',
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  }
];

// ============================================
// MOCK DATA - RELATED LISTS (UI-2 PHASE 7)
// ============================================

const MOCK_RELATED_LISTS: RelatedList[] = [
  {
    id: 'rellist_001',
    layoutId: 'layout_001',
    name: 'relatedCases',
    label: 'Related Cases',
    relatedObject: 'case',
    relationshipField: 'clientId__c',
    order: 1,
    columns: ['caseNumber__c', 'caseStatus__c', 'priority__c', 'createdDate__c'],
    sortField: 'createdDate__c',
    sortDirection: 'desc',
    rowLimit: 10,
    showNewButton: true,
    showViewAllLink: true,
    enabled: true
  },
  {
    id: 'rellist_002',
    layoutId: 'layout_001',
    name: 'relatedAudits',
    label: 'CyberSweep Audits',
    relatedObject: 'audit',
    relationshipField: 'clientId__c',
    order: 2,
    columns: ['auditDate__c', 'auditType__c', 'findings__c', 'status__c'],
    sortField: 'auditDate__c',
    sortDirection: 'desc',
    rowLimit: 5,
    showNewButton: true,
    showViewAllLink: true,
    enabled: true
  },
  {
    id: 'rellist_003',
    layoutId: 'layout_001',
    name: 'relatedContacts',
    label: 'Contacts',
    relatedObject: 'contact',
    relationshipField: 'clientId__c',
    order: 3,
    columns: ['name__c', 'email__c', 'phone__c', 'role__c'],
    sortField: 'name__c',
    sortDirection: 'asc',
    rowLimit: 10,
    showNewButton: true,
    showViewAllLink: false,
    enabled: true
  }
];

// ============================================
// MOCK DATA - QUICK ACTIONS (UI-2 PHASE 7)
// ============================================

const MOCK_QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'action_001',
    layoutId: 'layout_001',
    name: 'createNewCase',
    label: 'Create New Case',
    type: 'standard',
    order: 1,
    icon: '➕',
    description: 'Create a new case for this client',
    enabled: true,
    visibleForRoles: ['system_admin', 'cs_supervisor', 'cs_analyst']
  },
  {
    id: 'action_002',
    layoutId: 'layout_001',
    name: 'sendWelcomeEmail',
    label: 'Send Welcome Email',
    type: 'custom',
    order: 2,
    icon: '📧',
    description: 'Send welcome email to client',
    confirmationMessage: 'Send welcome email to this client?',
    successMessage: 'Welcome email sent successfully',
    enabled: true
  },
  {
    id: 'action_003',
    layoutId: 'layout_001',
    name: 'scheduleAudit',
    label: 'Schedule Audit',
    type: 'standard',
    order: 3,
    icon: '📅',
    description: 'Schedule a new CyberSweep audit',
    enabled: true
  },
  {
    id: 'action_004',
    layoutId: 'layout_001',
    name: 'viewClientPortal',
    label: 'View Client Portal',
    type: 'link',
    order: 4,
    icon: '🔗',
    description: 'Open client portal in new window',
    url: '/portal/client/{clientId}',
    openInNewWindow: true,
    enabled: true
  },
  {
    id: 'action_005',
    layoutId: 'layout_001',
    name: 'exportClientData',
    label: 'Export Data',
    type: 'custom',
    order: 5,
    icon: '📤',
    description: 'Export client data to CSV',
    successMessage: 'Export started. You will receive an email when ready.',
    enabled: true,
    visibleForRoles: ['system_admin', 'cs_supervisor']
  }
];

// ============================================
// MOCK DATA - CUSTOM BUTTONS (UI-2 PHASE 7)
// ============================================

const MOCK_CUSTOM_BUTTONS: CustomButton[] = [
  {
    id: 'button_001',
    layoutId: 'layout_001',
    sectionId: 'section_001',
    name: 'verifyEmail',
    label: 'Verify Email',
    type: 'custom',
    order: 1,
    icon: '✓',
    description: 'Send verification email',
    style: 'primary',
    size: 'sm',
    enabled: true
  },
  {
    id: 'button_002',
    layoutId: 'layout_001',
    name: 'printSummary',
    label: 'Print Summary',
    type: 'javascript',
    order: 2,
    icon: '📄',
    description: 'Print client summary',
    script: 'window.print();',
    style: 'secondary',
    size: 'md',
    enabled: true
  }
];

// ============================================
// MOCK DATA - LAYOUT ASSIGNMENTS
// ============================================

const MOCK_LAYOUT_ASSIGNMENTS: LayoutAssignment[] = [
  {
    id: 'assign_001',
    layoutId: 'layout_001',
    profileId: 'profile_admin',
    profileName: 'System Administrator',
    isDefault: false,
    priority: 1,
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  },
  {
    id: 'assign_002',
    layoutId: 'layout_001',
    profileId: 'profile_cs_supervisor',
    profileName: 'CyberSweep Supervisor',
    isDefault: false,
    priority: 2,
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  },
  {
    id: 'assign_003',
    layoutId: 'layout_001',
    recordType: 'standard_client',
    isDefault: true,
    priority: 10,
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z'
  },
  {
    id: 'assign_004',
    layoutId: 'layout_002',
    recordType: 'enterprise_client',
    isDefault: false,
    priority: 5,
    createdAt: '2025-01-11T00:00:00Z',
    updatedAt: '2025-01-11T00:00:00Z'
  }
];

// ============================================
// MOCK DATA - DYNAMIC TABS
// ============================================

const MOCK_DYNAMIC_TABS: DynamicTab[] = [
  // CyberSweep Client Detail Tabs
  {
    id: 'tab_001',
    page: 'cybersweep_client_detail',
    name: 'Overview',
    key: 'overview',
    icon: 'info',
    order: 1,
    visible: true,
    application: 'cybersweep'
  },
  {
    id: 'tab_002',
    page: 'cybersweep_client_detail',
    name: 'Audits',
    key: 'audits',
    icon: 'clipboard',
    order: 2,
    visible: true,
    application: 'cybersweep'
  },
  {
    id: 'tab_003',
    page: 'cybersweep_client_detail',
    name: 'PII Configuration',
    key: 'pii-config',
    icon: 'shield',
    order: 3,
    visible: true,
    application: 'cybersweep'
  },
  {
    id: 'tab_004',
    page: 'cybersweep_client_detail',
    name: 'History',
    key: 'history',
    icon: 'clock',
    order: 4,
    visible: true,
    application: 'cybersweep'
  },

  // Client Object Tabs
  {
    id: 'tab_client_001',
    page: 'Client__c',
    name: 'Details',
    key: 'details',
    icon: 'User',
    order: 1,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_client_002',
    page: 'Client__c',
    name: 'Related Contacts',
    key: 'related-contacts',
    icon: 'Users',
    order: 2,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_client_003',
    page: 'Client__c',
    name: 'Activity',
    key: 'activity',
    icon: 'Activity',
    order: 3,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_client_004',
    page: 'Client__c',
    name: 'Files',
    key: 'files',
    icon: 'FileText',
    order: 4,
    visible: true,
    application: 'global'
  },

  // Contact Object Tabs
  {
    id: 'tab_contact_001',
    page: 'Contact__c',
    name: 'Information',
    key: 'information',
    icon: 'User',
    order: 1,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_contact_002',
    page: 'Contact__c',
    name: 'Related Accounts',
    key: 'related-accounts',
    icon: 'Database',
    order: 2,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_contact_003',
    page: 'Contact__c',
    name: 'Communication History',
    key: 'communication-history',
    icon: 'MessageSquare',
    order: 3,
    visible: true,
    application: 'global'
  },

  // Account Object Tabs
  {
    id: 'tab_account_001',
    page: 'Account__c',
    name: 'Overview',
    key: 'overview',
    icon: 'Database',
    order: 1,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_account_002',
    page: 'Account__c',
    name: 'Contacts',
    key: 'contacts',
    icon: 'Users',
    order: 2,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_account_003',
    page: 'Account__c',
    name: 'Opportunities',
    key: 'opportunities',
    icon: 'Star',
    order: 3,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_account_004',
    page: 'Account__c',
    name: 'Documents',
    key: 'documents',
    icon: 'File',
    order: 4,
    visible: true,
    application: 'global'
  },

  // Case Object Tabs
  {
    id: 'tab_case_001',
    page: 'Case__c',
    name: 'Case Details',
    key: 'case-details',
    icon: 'FileText',
    order: 1,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_case_002',
    page: 'Case__c',
    name: 'Resolution',
    key: 'resolution',
    icon: 'CheckCircle',
    order: 2,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_case_003',
    page: 'Case__c',
    name: 'Related Items',
    key: 'related-items',
    icon: 'Link',
    order: 3,
    visible: true,
    application: 'global'
  },

  // Product Object Tabs
  {
    id: 'tab_product_001',
    page: 'Product__c',
    name: 'Product Info',
    key: 'product-info',
    icon: 'Package',
    order: 1,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_product_002',
    page: 'Product__c',
    name: 'Pricing',
    key: 'pricing',
    icon: 'DollarSign',
    order: 2,
    visible: true,
    application: 'global'
  },
  {
    id: 'tab_product_003',
    page: 'Product__c',
    name: 'Related Accounts',
    key: 'related-accounts',
    icon: 'Database',
    order: 3,
    visible: true,
    application: 'global'
  }
];

// ============================================
// MOCK DATA - VERSION HISTORY
// ============================================

const MOCK_LAYOUT_VERSIONS: PageLayoutVersion[] = [
  {
    id: 'version_001',
    templateId: 'template_001',
    version: 1,
    changes: 'Initial layout creation',
    sections: [], // Would contain snapshot of sections
    createdAt: '2025-01-01T00:00:00Z',
    createdBy: 'system'
  }
];

// ===========================
// HELPER FUNCTIONS
// ===========================

/**
 * Auto-add __c suffix to field keys if not present
 * This ensures Salesforce compatibility
 */
const ensureSalesforceSuffix = (fieldKey: string): string => {
  if (!fieldKey.endsWith('__c')) {
    return `${fieldKey}__c`;
  }
  return fieldKey;
};

/**
 * Generate unique field ID
 */
const generateFieldId = (): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `field_${timestamp}_${random}`;
};

/**
 * Apply filters to custom fields array
 */
const applyFilters = (fields: CustomField[], filters?: CustomFieldFilters): CustomField[] => {
  if (!filters) return fields;

  let filtered = [...fields];

  if (filters.application) {
    filtered = filtered.filter(f => f.application === filters.application);
  }

  if (filters.section) {
    filtered = filtered.filter(f => f.section === filters.section);
  }

  if (filters.type) {
    filtered = filtered.filter(f => f.type === filters.type);
  }

  if (filters.isActive !== undefined) {
    filtered = filtered.filter(f => f.isActive === filters.isActive);
  }

  if (filters.isSystem !== undefined) {
    filtered = filtered.filter(f => f.isSystem === filters.isSystem);
  }

  if (filters.searchTerm) {
    const term = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(f =>
      f.label.toLowerCase().includes(term) ||
      f.fieldKey.toLowerCase().includes(term)
    );
  }

  return filtered;
};

/**
 * Calculate field statistics
 */
const calculateStatistics = (fields: CustomField[]): FieldStatistics => {
  const stats: FieldStatistics = {
    totalFields: fields.length,
    activeFields: fields.filter(f => f.isActive).length,
    inactiveFields: fields.filter(f => !f.isActive).length,
    systemFields: fields.filter(f => f.isSystem).length,
    customFields: fields.filter(f => !f.isSystem).length,
    byApplication: {} as Record<Application, number>,
    byType: {} as Record<FieldType, number>,
    bySection: {} as Record<FieldSection, number>,
    recentlyCreated: [],
    recentlyUpdated: []
  };

  // Count by application
  fields.forEach(field => {
    stats.byApplication[field.application] = (stats.byApplication[field.application] || 0) + 1;
  });

  // Count by type
  fields.forEach(field => {
    stats.byType[field.type] = (stats.byType[field.type] || 0) + 1;
  });

  // Count by section
  fields.forEach(field => {
    stats.bySection[field.section] = (stats.bySection[field.section] || 0) + 1;
  });

  // Recently created (last 5)
  stats.recentlyCreated = [...fields]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // Recently updated (last 5)
  stats.recentlyUpdated = [...fields]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  return stats;
};

// ===========================
// API FUNCTIONS
// ===========================

/**
 * Fetch custom fields with optional filtering
 *
 * @param filters - Optional filter criteria
 * @returns Promise with filtered custom fields
 *
 * @example
 * const { data } = await fetchCustomFields({ application: 'cybersweep', isActive: true });
 */
export const fetchCustomFields = async (
  filters?: CustomFieldFilters
): Promise<{ data: CustomField[] }> => {
  try {
    // TODO: Replace with actual backend API call
    // const response = await axios.get('/api/ui-configuration/fields', { params: filters });
    // console.log('✅ Custom fields fetched from BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network delay
    const filtered = applyFilters(mockCustomFields, filters);
    console.log('📦 Custom fields fetched from MOCK DATA (in-memory)', {
      total: filtered.length,
      filters
    });
    return { data: filtered };
  } catch (error) {
    console.error('❌ Error fetching custom fields:', error);
    throw error;
  }
};

/**
 * Fetch a single custom field by ID
 *
 * @param fieldId - The field ID to fetch
 * @returns Promise with the custom field
 *
 * @example
 * const { data } = await fetchCustomField('field_001');
 */
export const fetchCustomField = async (
  fieldId: string
): Promise<{ data: CustomField }> => {
  try {
    // TODO: Replace with actual backend API call
    // const response = await axios.get(`/api/ui-configuration/fields/${fieldId}`);
    // console.log('✅ Custom field fetched from BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 200));
    const field = mockCustomFields.find(f => f.id === fieldId);

    if (!field) {
      throw new Error(`Custom field with ID ${fieldId} not found`);
    }

    console.log('📦 Custom field fetched from MOCK DATA (in-memory)', field);
    return { data: field };
  } catch (error) {
    console.error('❌ Error fetching custom field:', error);
    throw error;
  }
};

/**
 * Create a new custom field
 *
 * @param fieldData - The field data to create
 * @returns Promise with the created field and success message
 *
 * @example
 * const { data } = await createCustomField({
 *   fieldKey: 'custom_field',
 *   label: 'My Custom Field',
 *   type: 'text',
 *   application: 'cybersweep',
 *   section: 'custom',
 *   isRequired: false,
 *   isActive: true,
 *   displayOrder: 100
 * });
 */
export const createCustomField = async (
  fieldData: Omit<CustomField, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>
): Promise<{ data: CustomField; message: string }> => {
  try {
    // Auto-add __c suffix
    const fieldKey = ensureSalesforceSuffix(fieldData.fieldKey);

    // Validate uniqueness
    const exists = mockCustomFields.some(f => f.fieldKey === fieldKey);
    if (exists) {
      throw new Error(`Field with key "${fieldKey}" already exists`);
    }

    // TODO: Replace with actual backend API call
    // const response = await axios.post('/api/ui-configuration/fields', { ...fieldData, fieldKey });
    // console.log('✅ Custom field created via BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 400));

    const newField: CustomField = {
      ...fieldData,
      id: generateFieldId(),
      fieldKey,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current_user' // Would be actual user ID in production
    };

    mockCustomFields.push(newField);

    console.log('📦 Custom field created in MOCK DATA (in-memory)', newField);
    return {
      data: newField,
      message: 'Custom field created successfully'
    };
  } catch (error) {
    console.error('❌ Error creating custom field:', error);
    throw error;
  }
};

/**
 * Update an existing custom field
 *
 * @param fieldId - The field ID to update
 * @param updates - Partial field data to update
 * @returns Promise with the updated field and success message
 *
 * @example
 * const { data } = await updateCustomField('field_001', {
 *   label: 'Updated Label',
 *   isActive: false
 * });
 */
export const updateCustomField = async (
  fieldId: string,
  updates: Partial<Omit<CustomField, 'id' | 'createdAt' | 'createdBy'>>
): Promise<{ data: CustomField; message: string }> => {
  try {
    // If updating fieldKey, ensure __c suffix
    let processedUpdates = { ...updates };
    if (updates.fieldKey) {
      processedUpdates.fieldKey = ensureSalesforceSuffix(updates.fieldKey);

      // Validate uniqueness if changing fieldKey
      const exists = mockCustomFields.some(f =>
        f.id !== fieldId && f.fieldKey === processedUpdates.fieldKey
      );
      if (exists) {
        throw new Error(`Field with key "${processedUpdates.fieldKey}" already exists`);
      }
    }

    // TODO: Replace with actual backend API call
    // const response = await axios.patch(`/api/ui-configuration/fields/${fieldId}`, processedUpdates);
    // console.log('✅ Custom field updated via BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 400));

    const fieldIndex = mockCustomFields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) {
      throw new Error(`Custom field with ID ${fieldId} not found`);
    }

    const updatedField: CustomField = {
      ...mockCustomFields[fieldIndex],
      ...processedUpdates,
      updatedAt: new Date().toISOString(),
      updatedBy: 'current_user'
    };

    mockCustomFields[fieldIndex] = updatedField;

    console.log('📦 Custom field updated in MOCK DATA (in-memory)', updatedField);
    return {
      data: updatedField,
      message: 'Custom field updated successfully'
    };
  } catch (error) {
    console.error('❌ Error updating custom field:', error);
    throw error;
  }
};

/**
 * Delete a custom field
 *
 * @param fieldId - The field ID to delete
 * @returns Promise with success message
 *
 * @example
 * const { message } = await deleteCustomField('field_custom_001');
 */
export const deleteCustomField = async (
  fieldId: string
): Promise<{ message: string }> => {
  try {
    // Check if field is system field
    const field = mockCustomFields.find(f => f.id === fieldId);
    if (!field) {
      throw new Error(`Custom field with ID ${fieldId} not found`);
    }

    if (field.isSystem) {
      throw new Error('Cannot delete system fields');
    }

    // TODO: Replace with actual backend API call
    // const response = await axios.delete(`/api/ui-configuration/fields/${fieldId}`);
    // console.log('✅ Custom field deleted via BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 300));

    mockCustomFields = mockCustomFields.filter(f => f.id !== fieldId);

    console.log('📦 Custom field deleted from MOCK DATA (in-memory)', { fieldId });
    return {
      message: 'Custom field deleted successfully'
    };
  } catch (error) {
    console.error('❌ Error deleting custom field:', error);
    throw error;
  }
};

/**
 * Fetch field statistics
 *
 * @returns Promise with field statistics
 *
 * @example
 * const { data } = await fetchFieldStatistics();
 */
export const fetchFieldStatistics = async (): Promise<{ data: FieldStatistics }> => {
  try {
    // TODO: Replace with actual backend API call
    // const response = await axios.get('/api/ui-configuration/fields/statistics');
    // console.log('✅ Field statistics fetched from BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 200));
    const stats = calculateStatistics(mockCustomFields);

    console.log('📦 Field statistics calculated from MOCK DATA (in-memory)', stats);
    return { data: stats };
  } catch (error) {
    console.error('❌ Error fetching field statistics:', error);
    throw error;
  }
};

/**
 * Validate field key uniqueness
 *
 * @param fieldKey - The field key to validate
 * @param excludeFieldId - Optional field ID to exclude from check (for updates)
 * @returns Promise with validation result
 *
 * @example
 * const { isValid, message } = await validateFieldKey('new_field__c');
 */
export const validateFieldKey = async (
  fieldKey: string,
  excludeFieldId?: string
): Promise<{ isValid: boolean; message: string; normalizedKey: string }> => {
  try {
    const normalizedKey = ensureSalesforceSuffix(fieldKey);

    // TODO: Replace with actual backend API call
    // const response = await axios.post('/api/ui-configuration/fields/validate', {
    //   fieldKey: normalizedKey,
    //   excludeFieldId
    // });
    // console.log('✅ Field key validated via BACKEND API');
    // return response.data;

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 150));

    const exists = mockCustomFields.some(f =>
      f.fieldKey === normalizedKey && f.id !== excludeFieldId
    );

    const result = {
      isValid: !exists,
      message: exists
        ? `Field key "${normalizedKey}" is already in use`
        : `Field key "${normalizedKey}" is available`,
      normalizedKey
    };

    console.log('📦 Field key validated with MOCK DATA (in-memory)', result);
    return result;
  } catch (error) {
    console.error('❌ Error validating field key:', error);
    throw error;
  }
};

// ============================================
// API CONFIGURATION FOR PAGE LAYOUTS
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  // Add authentication headers when needed
  // 'Authorization': `Bearer ${getAuthToken()}`
});

// ============================================
// PAGE LAYOUT TEMPLATES API FUNCTIONS
// ============================================

// Fetch all page layout templates
export const fetchPageLayoutTemplates = async (filters?: {
  application?: Application;
  objectId?: string;
}): Promise<{ data: PageLayoutTemplate[] }> => {
  console.log('🔍 Fetching page layout templates...', filters);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      const objToBase: Record<string, string> = {
        obj_lead: 'Lead', obj_client: 'Client', obj_contact: 'Contact',
        obj_opportunity: 'Opportunity', obj_case: 'Case', obj_event: 'Event',
        obj_task: 'Task', obj_report: 'Report',
        obj_csaudit: 'CSAudit', obj_pawrecord: 'PAWRecord', obj_cybersweepclient: 'CyberSweepClient',
      };
      let objectTypeKey = filters?.objectId && objToBase[filters.objectId]
        ? normalizeToGobodoFormat(objToBase[filters.objectId])
        : undefined;
      if (!objectTypeKey && filters?.objectId?.startsWith('obj_')) {
        const base = filters.objectId.replace(/^obj_/, '');
        const words = base.split('_');
        const pascal = words.map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
        objectTypeKey = normalizeToGobodoFormat(pascal);
      }
      return await gobodoPageLayoutService.fetchPageLayoutTemplates({
        application: filters?.application,
        objectTypeKey: objectTypeKey || filters?.objectId,
      });
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const params = new URLSearchParams();
    if (filters?.application) params.append('application', filters.application);

    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Page layout templates loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock page layout templates');
  }

  // Mock data with filtering
  await new Promise(resolve => setTimeout(resolve, 150));

  let filtered = [...MOCK_PAGE_LAYOUT_TEMPLATES];

  if (filters?.application) {
    filtered = filtered.filter(t => t.application === filters.application);
  }

  return { data: filtered };
};

// Fetch single template
export const fetchPageLayoutTemplate = async (templateId: string): Promise<{ data: PageLayoutTemplate }> => {
  console.log('🔍 Fetching page layout template:', templateId);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API for page layout template');
      return await gobodoPageLayoutService.fetchPageLayoutTemplate(templateId);
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates/${templateId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Page layout template loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock template');
  }

  await new Promise(resolve => setTimeout(resolve, 100));

  const template = MOCK_PAGE_LAYOUT_TEMPLATES.find(t => t.id === templateId);
  if (!template) {
    throw new Error('Template not found');
  }

  return { data: template };
};

// Create page layout template
export const createPageLayoutTemplate = async (
  templateData: Omit<PageLayoutTemplate, 'id' | 'version' | 'createdAt' | 'updatedAt' | 'createdBy'>
): Promise<{ data: PageLayoutTemplate; message: string }> => {
  console.log('✨ Creating page layout template:', templateData);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to create page layout template');
      const result = await gobodoPageLayoutService.createPageLayoutTemplate({
        ...templateData,
        version: 1,
        createdBy: 'current_user'
      });
      return {
        data: result.data,
        message: 'Page layout template created successfully'
      };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData)
    });

    if (response.ok) {
      console.log('✅ Page layout template created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newTemplate: PageLayoutTemplate = {
    ...templateData,
    id: `template_${Date.now()}`,
    version: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'current_user'
  };

  MOCK_PAGE_LAYOUT_TEMPLATES.push(newTemplate);

  return {
    data: newTemplate,
    message: 'Page layout template created successfully'
  };
};

// Update page layout template
export const updatePageLayoutTemplate = async (
  templateId: string,
  updates: Partial<Omit<PageLayoutTemplate, 'id' | 'createdAt' | 'createdBy'>>
): Promise<{ data: PageLayoutTemplate; message: string }> => {
  console.log('💾 Updating page layout template:', templateId, updates);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to update page layout template');
      const result = await gobodoPageLayoutService.updatePageLayoutTemplate(templateId, updates);
      return {
        data: result.data,
        message: 'Page layout template updated successfully'
      };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates/${templateId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Page layout template updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const templateIndex = MOCK_PAGE_LAYOUT_TEMPLATES.findIndex(t => t.id === templateId);
  if (templateIndex === -1) {
    throw new Error('Template not found');
  }

  const updatedTemplate: PageLayoutTemplate = {
    ...MOCK_PAGE_LAYOUT_TEMPLATES[templateIndex],
    ...updates,
    version: (MOCK_PAGE_LAYOUT_TEMPLATES[templateIndex].version || 1) + 1,
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user'
  };

  MOCK_PAGE_LAYOUT_TEMPLATES[templateIndex] = updatedTemplate;

  // Create version history entry
  MOCK_LAYOUT_VERSIONS.push({
    id: `version_${Date.now()}`,
    templateId,
    version: updatedTemplate.version,
    changes: 'Template updated',
    sections: updatedTemplate.sections,
    createdAt: new Date().toISOString(),
    createdBy: 'current_user'
  });

  return {
    data: updatedTemplate,
    message: 'Page layout template updated successfully'
  };
};

// Clone page layout template
export const clonePageLayoutTemplate = async (
  templateId: string,
  newName: string
): Promise<{ data: PageLayoutTemplate; message: string }> => {
  console.log('📋 Cloning page layout template:', templateId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates/${templateId}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name: newName })
    });

    if (response.ok) {
      console.log('✅ Page layout template cloned successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template cloning');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const originalTemplate = MOCK_PAGE_LAYOUT_TEMPLATES.find(t => t.id === templateId);
  if (!originalTemplate) {
    throw new Error('Template not found');
  }

  const clonedTemplate: PageLayoutTemplate = {
    ...originalTemplate,
    id: `template_${Date.now()}`,
    name: newName,
    isDefault: false,
    version: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'current_user',
    updatedBy: undefined
  };

  MOCK_PAGE_LAYOUT_TEMPLATES.push(clonedTemplate);

  return {
    data: clonedTemplate,
    message: 'Page layout template cloned successfully'
  };
};

// Delete page layout template
export const deletePageLayoutTemplate = async (templateId: string): Promise<{ message: string }> => {
  console.log('🗑️ Deleting page layout template:', templateId);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to delete page layout template');
      await gobodoPageLayoutService.deletePageLayoutTemplate(templateId);
      return { message: 'Page layout template deleted successfully' };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates/${templateId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Page layout template deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const templateIndex = MOCK_PAGE_LAYOUT_TEMPLATES.findIndex(t => t.id === templateId);
  if (templateIndex === -1) {
    throw new Error('Template not found');
  }

  MOCK_PAGE_LAYOUT_TEMPLATES.splice(templateIndex, 1);

  return {
    message: 'Page layout template deleted successfully'
  };
};

// Fetch version history for a template
export const fetchTemplateVersionHistory = async (templateId: string): Promise<{ data: PageLayoutVersion[] }> => {
  console.log('🔍 Fetching template version history:', templateId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates/${templateId}/versions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Template version history loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock version history');
  }

  await new Promise(resolve => setTimeout(resolve, 100));

  const versions = MOCK_LAYOUT_VERSIONS.filter(v => v.templateId === templateId);

  return { data: versions };
};

// Rollback to previous version
export const rollbackTemplateVersion = async (
  templateId: string,
  versionId: string
): Promise<{ data: PageLayoutTemplate; message: string }> => {
  console.log('⏪ Rolling back template to version:', versionId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/templates/${templateId}/rollback`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ versionId })
    });

    if (response.ok) {
      console.log('✅ Template rolled back successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rollback');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const version = MOCK_LAYOUT_VERSIONS.find(v => v.id === versionId);
  if (!version) {
    throw new Error('Version not found');
  }

  const templateIndex = MOCK_PAGE_LAYOUT_TEMPLATES.findIndex(t => t.id === templateId);
  if (templateIndex === -1) {
    throw new Error('Template not found');
  }

  const rolledBackTemplate: PageLayoutTemplate = {
    ...MOCK_PAGE_LAYOUT_TEMPLATES[templateIndex],
    sections: version.sections,
    version: (MOCK_PAGE_LAYOUT_TEMPLATES[templateIndex].version || 1) + 1,
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user'
  };

  MOCK_PAGE_LAYOUT_TEMPLATES[templateIndex] = rolledBackTemplate;

  return {
    data: rolledBackTemplate,
    message: 'Template rolled back successfully'
  };
};

// ============================================
// PAGE LAYOUT SECTION API FUNCTIONS (UI-2 PHASE 5)
// ============================================

// Fetch sections for a layout
export const fetchLayoutSections = async (layoutId: string): Promise<{ data: LayoutSection[] }> => {
  console.log('🔍 Fetching layout sections for:', layoutId);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API for layout sections');
      const sections = await gobodoPageLayoutService.fetchLayoutSections(layoutId);
      // Convert PageLayoutSection[] to LayoutSection[]
      const layoutSections: LayoutSection[] = sections.map((s: PageLayoutSection) => ({
        id: s.id,
        layoutId: layoutId,
        name: s.name,
        label: s.name,
        order: s.order,
        columns: s.columns as 1 | 2,
        collapsible: s.collapsible,
        collapsed: s.defaultCollapsed,
        fields: s.fields.map(f => ({
          id: f.fieldId,
          fieldId: f.fieldId,
          fieldName: f.fieldKey,
          fieldLabel: f.label,
          fieldType: f.type,
          order: f.order,
          column: 1 as 1 | 2,
          required: f.required,
          readonly: false,
          width: (f.width === 'full' ? 'full' : 'half') as 'full' | 'half'
        }))
      }));
      return { data: layoutSections };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Sections loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock sections');
  }

  await new Promise(resolve => setTimeout(resolve, 150));

  const sections = MOCK_LAYOUT_SECTIONS.filter(s => s.layoutId === layoutId);

  return { data: sections };
};

// Create new section
export const createLayoutSection = async (
  layoutId: string,
  sectionData: Omit<LayoutSection, 'id' | 'layoutId' | 'fields'>
): Promise<{ data: LayoutSection; message: string }> => {
  console.log('✨ Creating layout section:', sectionData);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to create layout section');
      // Convert LayoutSection to PageLayoutSection format
      const pageSection: Omit<PageLayoutSection, 'id' | 'createdAt' | 'updatedAt' | 'fields'> = {
        name: sectionData.name,
        description: sectionData.label || sectionData.name,
        createdBy: 'current_user',
        page: 'default',
        tab: 'details',
        columns: sectionData.columns as 1 | 2 | 3 | 4,
        order: sectionData.order,
        collapsible: sectionData.collapsible,
        defaultCollapsed: sectionData.collapsed,
        visible: true
      };
      const result = await gobodoPageLayoutService.createLayoutSection(layoutId, pageSection);
      // Convert back to LayoutSection
      const layoutSection: LayoutSection = {
        id: result.data.id,
        layoutId: layoutId,
        name: result.data.name,
        label: result.data.name,
        order: result.data.order,
        columns: result.data.columns as 1 | 2,
        collapsible: result.data.collapsible,
        collapsed: result.data.defaultCollapsed,
        fields: []
      };
      return { data: layoutSection, message: 'Section created successfully' };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
      // Fall through to REST API or mock
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(sectionData)
    });

    if (response.ok) {
      console.log('✅ Section created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating section creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newSection: LayoutSection = {
    ...sectionData,
    id: `section_${Date.now()}`,
    layoutId,
    fields: []
  };

  MOCK_LAYOUT_SECTIONS.push(newSection);

  return {
    data: newSection,
    message: 'Section created successfully'
  };
};

// Update section
export const updateLayoutSection = async (
  layoutId: string,
  sectionId: string,
  updates: Partial<LayoutSection>
): Promise<{ data: LayoutSection; message: string }> => {
  console.log('💾 Updating layout section:', sectionId, updates);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to update layout section');
      const pageSection: Partial<PageLayoutSection> = {
        name: updates.name,
        description: updates.label,
        columns: updates.columns as 1 | 2 | 3 | 4,
        order: updates.order,
        collapsible: updates.collapsible,
        defaultCollapsed: updates.collapsed,
        visible: true
      };
      const result = await gobodoPageLayoutService.updateLayoutSection(layoutId, sectionId, pageSection);
      const layoutSection: LayoutSection = {
        id: result.data.id,
        layoutId: layoutId,
        name: result.data.name,
        label: result.data.name,
        order: result.data.order,
        columns: result.data.columns as 1 | 2,
        collapsible: result.data.collapsible,
        collapsed: result.data.defaultCollapsed,
        fields: result.data.fields.map((f: PageLayoutField) => ({
          id: f.fieldId,
          fieldId: f.fieldId,
          fieldName: f.fieldKey,
          fieldLabel: f.label,
          fieldType: f.type,
          order: f.order,
          column: 1 as 1 | 2,
          required: f.required,
          readonly: false,
          width: (f.width === 'full' ? 'full' : 'half') as 'full' | 'half'
        }))
      };
      return { data: layoutSection, message: 'Section updated successfully' };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections/${sectionId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Section updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating section update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const sectionIndex = MOCK_LAYOUT_SECTIONS.findIndex(s => s.id === sectionId);
  if (sectionIndex === -1) {
    throw new Error('Section not found');
  }

  const updatedSection = {
    ...MOCK_LAYOUT_SECTIONS[sectionIndex],
    ...updates
  };

  MOCK_LAYOUT_SECTIONS[sectionIndex] = updatedSection;

  return {
    data: updatedSection,
    message: 'Section updated successfully'
  };
};

// Delete section
export const deleteLayoutSection = async (
  layoutId: string,
  sectionId: string
): Promise<{ message: string }> => {
  console.log('🗑️ Deleting layout section:', sectionId);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to delete layout section');
      await gobodoPageLayoutService.deleteLayoutSection(layoutId, sectionId);
      return { message: 'Section deleted successfully' };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections/${sectionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Section deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating section deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const sectionIndex = MOCK_LAYOUT_SECTIONS.findIndex(s => s.id === sectionId);
  if (sectionIndex === -1) {
    throw new Error('Section not found');
  }

  MOCK_LAYOUT_SECTIONS.splice(sectionIndex, 1);

  return {
    message: 'Section deleted successfully'
  };
};

// Add field to section
export const addFieldToSection = async (
  layoutId: string,
  sectionId: string,
  fieldData: Omit<SectionField, 'id'>
): Promise<{ data: SectionField; message: string }> => {
  console.log('➕ Adding field to section:', sectionId, fieldData);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to add field to section');
      const pageField: Partial<PageLayoutField> = {
        fieldId: fieldData.fieldId,
        label: fieldData.fieldLabel,
        fieldKey: fieldData.fieldName,
        type: fieldData.fieldType as any,
        required: fieldData.required,
        order: fieldData.order,
        width: fieldData.width === 'full' ? 'full' : 'half'
      };
      const result = await gobodoPageLayoutService.addFieldToSection(layoutId, sectionId, pageField);
      const sectionField: SectionField = {
        id: fieldData.fieldId,
        fieldId: result.data.fieldId,
        fieldName: result.data.fieldKey,
        fieldLabel: result.data.label,
        fieldType: result.data.type,
        order: result.data.order,
        column: fieldData.column,
        required: result.data.required,
        readonly: false,
        width: result.data.width === 'full' ? 'full' : 'half'
      };
      return { data: sectionField, message: 'Field added to section successfully' };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections/${sectionId}/fields`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(fieldData)
    });

    if (response.ok) {
      console.log('✅ Field added to section successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating field addition');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const section = MOCK_LAYOUT_SECTIONS.find(s => s.id === sectionId);
  if (!section) {
    throw new Error('Section not found');
  }

  const newField: SectionField = {
    ...fieldData,
    id: `field_${Date.now()}`
  };

  section.fields.push(newField);

  return {
    data: newField,
    message: 'Field added to section successfully'
  };
};

// Remove field from section
export const removeFieldFromSection = async (
  layoutId: string,
  sectionId: string,
  fieldId: string
): Promise<{ message: string }> => {
  console.log('➖ Removing field from section:', sectionId, fieldId);

  // Use Gobodo if enabled
  if (USE_GOBODO_API && gobodoPageLayoutService) {
    try {
      console.log('✅ Using Gobodo API to remove field from section');
      await gobodoPageLayoutService.removeFieldFromSection(layoutId, sectionId, fieldId);
      return { message: 'Field removed from section successfully' };
    } catch (error: any) {
      console.error('❌ Gobodo API error, falling back to REST/mock:', error);
    }
  }

  // Fallback to REST API or mock
  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections/${sectionId}/fields/${fieldId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Field removed from section successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating field removal');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const section = MOCK_LAYOUT_SECTIONS.find(s => s.id === sectionId);
  if (!section) {
    throw new Error('Section not found');
  }

  const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
  if (fieldIndex === -1) {
    throw new Error('Field not found in section');
  }

  section.fields.splice(fieldIndex, 1);

  return {
    message: 'Field removed from section successfully'
  };
};

// Reorder fields in section
export const reorderSectionFields = async (
  layoutId: string,
  sectionId: string,
  fieldOrders: { fieldId: string; order: number; column: number }[]
): Promise<{ message: string }> => {
  console.log('🔄 Reordering section fields:', sectionId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/sections/${sectionId}/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ fieldOrders })
    });

    if (response.ok) {
      console.log('✅ Fields reordered successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating field reorder');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const section = MOCK_LAYOUT_SECTIONS.find(s => s.id === sectionId);
  if (!section) {
    throw new Error('Section not found');
  }

  // Update field orders
  fieldOrders.forEach(({ fieldId, order, column }) => {
    const field = section.fields.find(f => f.id === fieldId);
    if (field) {
      field.order = order;
      field.column = column as 1 | 2;
    }
  });

  // Sort fields by order
  section.fields.sort((a, b) => a.order - b.order);

  return {
    message: 'Fields reordered successfully'
  };
};

// ============================================
// VISIBILITY RULE API FUNCTIONS (UI-2 PHASE 6)
// ============================================

// Fetch visibility rules for a layout
export const fetchVisibilityRules = async (layoutId: string): Promise<{ data: VisibilityRule[]; _usingMockData?: boolean }> => {
  console.log('🔍 Fetching visibility rules for layout:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Visibility rules loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock visibility rules');
  }

  await new Promise(resolve => setTimeout(resolve, 150));

  const rules = MOCK_VISIBILITY_RULES
    .filter(r => r.layoutId === layoutId)
    .sort((a, b) => a.priority - b.priority);

  return { data: rules, _usingMockData: true };
};

// Create visibility rule
export const createVisibilityRule = async (
  layoutId: string,
  ruleData: Omit<VisibilityRule, 'id' | 'layoutId' | 'createdAt' | 'updatedAt'>
): Promise<{ data: VisibilityRule; message: string; _usingMockData?: boolean }> => {
  console.log('✨ Creating visibility rule:', ruleData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(ruleData)
    });

    if (response.ok) {
      console.log('✅ Visibility rule created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rule creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newRule: VisibilityRule = {
    ...ruleData,
    id: `rule_${Date.now()}`,
    layoutId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  MOCK_VISIBILITY_RULES.push(newRule);

  return {
    data: newRule,
    message: 'Visibility rule created successfully',
    _usingMockData: true
  };
};

// Update visibility rule
export const updateVisibilityRule = async (
  layoutId: string,
  ruleId: string,
  updates: Partial<VisibilityRule>
): Promise<{ data: VisibilityRule; message: string; _usingMockData?: boolean }> => {
  console.log('💾 Updating visibility rule:', ruleId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules/${ruleId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Visibility rule updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rule update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const ruleIndex = MOCK_VISIBILITY_RULES.findIndex(r => r.id === ruleId);
  if (ruleIndex === -1) {
    throw new Error('Visibility rule not found');
  }

  const updatedRule = {
    ...MOCK_VISIBILITY_RULES[ruleIndex],
    ...updates,
    updatedAt: new Date().toISOString()
  };

  MOCK_VISIBILITY_RULES[ruleIndex] = updatedRule;

  return {
    data: updatedRule,
    message: 'Visibility rule updated successfully',
    _usingMockData: true
  };
};

// Delete visibility rule
export const deleteVisibilityRule = async (
  layoutId: string,
  ruleId: string
): Promise<{ message: string; _usingMockData?: boolean }> => {
  console.log('🗑️ Deleting visibility rule:', ruleId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules/${ruleId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Visibility rule deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rule deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const ruleIndex = MOCK_VISIBILITY_RULES.findIndex(r => r.id === ruleId);
  if (ruleIndex === -1) {
    throw new Error('Visibility rule not found');
  }

  MOCK_VISIBILITY_RULES.splice(ruleIndex, 1);

  return {
    message: 'Visibility rule deleted successfully',
    _usingMockData: true
  };
};

// Toggle rule enabled status
export const toggleVisibilityRule = async (
  layoutId: string,
  ruleId: string,
  enabled: boolean
): Promise<{ data: VisibilityRule; message: string; _usingMockData?: boolean }> => {
  console.log('🔄 Toggling visibility rule:', ruleId, enabled);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules/${ruleId}/toggle`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ enabled })
    });

    if (response.ok) {
      console.log('✅ Visibility rule toggled successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rule toggle');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  const rule = MOCK_VISIBILITY_RULES.find(r => r.id === ruleId);
  if (!rule) {
    throw new Error('Visibility rule not found');
  }

  rule.enabled = enabled;
  rule.updatedAt = new Date().toISOString();

  return {
    data: rule,
    message: `Visibility rule ${enabled ? 'enabled' : 'disabled'} successfully`,
    _usingMockData: true
  };
};

// Evaluate rules (for preview/testing)
export const evaluateVisibilityRules = async (
  layoutId: string,
  fieldValues: Record<string, any>
): Promise<{ data: RuleEvaluationResult[]; _usingMockData?: boolean }> => {
  console.log('🧪 Evaluating visibility rules for layout:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules/evaluate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ fieldValues })
    });

    if (response.ok) {
      console.log('✅ Rules evaluated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rule evaluation');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  const rules = MOCK_VISIBILITY_RULES.filter(r => r.layoutId === layoutId && r.enabled);

  const results: RuleEvaluationResult[] = rules.map(rule => {
    // Simple evaluation logic
    let matched = false;

    if (rule.logic === 'AND') {
      matched = rule.conditions.every(cond => {
        const fieldValue = fieldValues[cond.fieldName];
        switch (cond.operator) {
          case 'equals':
            return fieldValue === cond.value;
          case 'not_equals':
            return fieldValue !== cond.value;
          case 'is_empty':
            return !fieldValue || fieldValue === '';
          case 'is_not_empty':
            return !!fieldValue && fieldValue !== '';
          case 'contains':
            return String(fieldValue).includes(String(cond.value));
          case 'greater_than':
            return Number(fieldValue) > Number(cond.value);
          case 'less_than':
            return Number(fieldValue) < Number(cond.value);
          default:
            return false;
        }
      });
    } else {
      // OR logic
      matched = rule.conditions.some(cond => {
        const fieldValue = fieldValues[cond.fieldName];
        switch (cond.operator) {
          case 'equals':
            return fieldValue === cond.value;
          case 'not_equals':
            return fieldValue !== cond.value;
          case 'is_empty':
            return !fieldValue || fieldValue === '';
          case 'is_not_empty':
            return !!fieldValue && fieldValue !== '';
          default:
            return false;
        }
      });
    }

    return {
      ruleId: rule.id,
      ruleName: rule.name,
      matched,
      action: rule.action,
      target: rule.target,
      targetId: rule.targetId,
      appliedAt: new Date().toISOString()
    };
  });

  return { data: results, _usingMockData: true };
};

// Reorder rules (change priority)
export const reorderVisibilityRules = async (
  layoutId: string,
  ruleOrders: { ruleId: string; priority: number }[]
): Promise<{ message: string; _usingMockData?: boolean }> => {
  console.log('🔄 Reordering visibility rules');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/rules/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ruleOrders })
    });

    if (response.ok) {
      console.log('✅ Rules reordered successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating rule reorder');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  ruleOrders.forEach(({ ruleId, priority }) => {
    const rule = MOCK_VISIBILITY_RULES.find(r => r.id === ruleId);
    if (rule) {
      rule.priority = priority;
      rule.updatedAt = new Date().toISOString();
    }
  });

  return {
    message: 'Rules reordered successfully',
    _usingMockData: true
  };
};

// ============================================
// RELATED LIST API FUNCTIONS (UI-2 PHASE 7)
// ============================================

// Fetch related lists for a layout
export const fetchRelatedLists = async (layoutId: string): Promise<{ data: RelatedList[]; _usingMockData?: boolean }> => {
  console.log('🔍 Fetching related lists for layout:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/related-lists`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Related lists loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock related lists');
  }

  await new Promise(resolve => setTimeout(resolve, 150));

  const lists = MOCK_RELATED_LISTS
    .filter(l => l.layoutId === layoutId)
    .sort((a, b) => a.order - b.order);

  return { data: lists, _usingMockData: true };
};

// Create related list
export const createRelatedList = async (
  layoutId: string,
  listData: Omit<RelatedList, 'id' | 'layoutId'>
): Promise<{ data: RelatedList; message: string; _usingMockData?: boolean }> => {
  console.log('✨ Creating related list:', listData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/related-lists`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(listData)
    });

    if (response.ok) {
      console.log('✅ Related list created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating related list creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newList: RelatedList = {
    ...listData,
    id: `rellist_${Date.now()}`,
    layoutId
  };

  MOCK_RELATED_LISTS.push(newList);

  return {
    data: newList,
    message: 'Related list created successfully',
    _usingMockData: true
  };
};

// Update related list
export const updateRelatedList = async (
  layoutId: string,
  listId: string,
  updates: Partial<RelatedList>
): Promise<{ data: RelatedList; message: string; _usingMockData?: boolean }> => {
  console.log('💾 Updating related list:', listId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/related-lists/${listId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Related list updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating related list update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const listIndex = MOCK_RELATED_LISTS.findIndex(l => l.id === listId);
  if (listIndex === -1) {
    throw new Error('Related list not found');
  }

  const updatedList = {
    ...MOCK_RELATED_LISTS[listIndex],
    ...updates
  };

  MOCK_RELATED_LISTS[listIndex] = updatedList;

  return {
    data: updatedList,
    message: 'Related list updated successfully',
    _usingMockData: true
  };
};

// Delete related list
export const deleteRelatedList = async (
  layoutId: string,
  listId: string
): Promise<{ message: string; _usingMockData?: boolean }> => {
  console.log('🗑️ Deleting related list:', listId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/related-lists/${listId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Related list deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating related list deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const listIndex = MOCK_RELATED_LISTS.findIndex(l => l.id === listId);
  if (listIndex === -1) {
    throw new Error('Related list not found');
  }

  MOCK_RELATED_LISTS.splice(listIndex, 1);

  return {
    message: 'Related list deleted successfully',
    _usingMockData: true
  };
};

// ============================================
// QUICK ACTION API FUNCTIONS (UI-2 PHASE 7)
// ============================================

// Fetch quick actions for a layout
export const fetchQuickActions = async (layoutId: string): Promise<{ data: QuickAction[]; _usingMockData?: boolean }> => {
  console.log('🔍 Fetching quick actions for layout:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/quick-actions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Quick actions loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock quick actions');
  }

  await new Promise(resolve => setTimeout(resolve, 150));

  const actions = MOCK_QUICK_ACTIONS
    .filter(a => a.layoutId === layoutId)
    .sort((a, b) => a.order - b.order);

  return { data: actions, _usingMockData: true };
};

// Create quick action
export const createQuickAction = async (
  layoutId: string,
  actionData: Omit<QuickAction, 'id' | 'layoutId'>
): Promise<{ data: QuickAction; message: string; _usingMockData?: boolean }> => {
  console.log('✨ Creating quick action:', actionData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/quick-actions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(actionData)
    });

    if (response.ok) {
      console.log('✅ Quick action created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating quick action creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newAction: QuickAction = {
    ...actionData,
    id: `action_${Date.now()}`,
    layoutId
  };

  MOCK_QUICK_ACTIONS.push(newAction);

  return {
    data: newAction,
    message: 'Quick action created successfully',
    _usingMockData: true
  };
};

// Update quick action
export const updateQuickAction = async (
  layoutId: string,
  actionId: string,
  updates: Partial<QuickAction>
): Promise<{ data: QuickAction; message: string; _usingMockData?: boolean }> => {
  console.log('💾 Updating quick action:', actionId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/quick-actions/${actionId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Quick action updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating quick action update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const actionIndex = MOCK_QUICK_ACTIONS.findIndex(a => a.id === actionId);
  if (actionIndex === -1) {
    throw new Error('Quick action not found');
  }

  const updatedAction = {
    ...MOCK_QUICK_ACTIONS[actionIndex],
    ...updates
  };

  MOCK_QUICK_ACTIONS[actionIndex] = updatedAction;

  return {
    data: updatedAction,
    message: 'Quick action updated successfully',
    _usingMockData: true
  };
};

// Delete quick action
export const deleteQuickAction = async (
  layoutId: string,
  actionId: string
): Promise<{ message: string; _usingMockData?: boolean }> => {
  console.log('🗑️ Deleting quick action:', actionId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/quick-actions/${actionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Quick action deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating quick action deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const actionIndex = MOCK_QUICK_ACTIONS.findIndex(a => a.id === actionId);
  if (actionIndex === -1) {
    throw new Error('Quick action not found');
  }

  MOCK_QUICK_ACTIONS.splice(actionIndex, 1);

  return {
    message: 'Quick action deleted successfully',
    _usingMockData: true
  };
};

// ============================================
// LAYOUT ASSIGNMENT API FUNCTIONS (UI-2 PHASE 8)
// ============================================

// Fetch layout assignments
export const fetchLayoutAssignments = async (layoutId: string): Promise<{ data: LayoutAssignment[]; _usingMockData?: boolean }> => {
  console.log('🔍 Fetching layout assignments for:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/assignments`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Assignments loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock assignments');
  }

  await new Promise(resolve => setTimeout(resolve, 150));

  const assignments = MOCK_LAYOUT_ASSIGNMENTS
    .filter(a => a.layoutId === layoutId)
    .sort((a, b) => a.priority - b.priority);

  return {
    data: assignments,
    _usingMockData: true
  };
};

// Create layout assignment
export const createLayoutAssignment = async (
  layoutId: string,
  assignmentData: Omit<LayoutAssignment, 'id' | 'layoutId' | 'createdAt' | 'updatedAt'>
): Promise<{ data: LayoutAssignment; message: string; _usingMockData?: boolean }> => {
  console.log('✨ Creating layout assignment:', assignmentData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/assignments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(assignmentData)
    });

    if (response.ok) {
      console.log('✅ Assignment created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating assignment creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newAssignment: LayoutAssignment = {
    ...assignmentData,
    id: `assign_${Date.now()}`,
    layoutId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  MOCK_LAYOUT_ASSIGNMENTS.push(newAssignment);

  return {
    data: newAssignment,
    message: 'Layout assignment created successfully',
    _usingMockData: true
  };
};

// Delete layout assignment
export const deleteLayoutAssignment = async (
  layoutId: string,
  assignmentId: string
): Promise<{ message: string; _usingMockData?: boolean }> => {
  console.log('🗑️ Deleting layout assignment:', assignmentId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/assignments/${assignmentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Assignment deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating assignment deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const assignmentIndex = MOCK_LAYOUT_ASSIGNMENTS.findIndex(a => a.id === assignmentId);
  if (assignmentIndex === -1) {
    throw new Error('Assignment not found');
  }

  MOCK_LAYOUT_ASSIGNMENTS.splice(assignmentIndex, 1);

  return {
    message: 'Layout assignment deleted successfully',
    _usingMockData: true
  };
};

// Preview layout with test data
export const previewLayout = async (
  layoutId: string,
  testData?: Record<string, any>
): Promise<{ data: LayoutPreviewData; _usingMockData?: boolean }> => {
  console.log('👁️ Previewing layout:', layoutId, 'with test data:', testData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/preview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ testData })
    });

    if (response.ok) {
      console.log('✅ Layout preview generated');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating layout preview');
  }

  await new Promise(resolve => setTimeout(resolve, 400));

  // Fetch all layout components
  const sectionsResponse = await fetchLayoutSections(layoutId);
  const sections = sectionsResponse.data;

  const relatedListsResponse = await fetchRelatedLists(layoutId);
  const relatedLists = relatedListsResponse.data;

  const quickActionsResponse = await fetchQuickActions(layoutId);
  const quickActions = quickActionsResponse.data;

  // Simple rule evaluation (show all sections by default)
  const visibleSections = sections.map(s => s.id);
  const hiddenSections: string[] = [];
  const requiredFields: string[] = [];
  const readonlyFields: string[] = [];

  const previewData: LayoutPreviewData = {
    layoutId,
    sections,
    visibleSections,
    hiddenSections,
    requiredFields,
    readonlyFields,
    relatedLists,
    quickActions,
    appliedRules: []
  };

  return {
    data: previewData,
    _usingMockData: true
  };
};

// Validate layout
export const validateLayout = async (layoutId: string): Promise<{ data: LayoutValidationResult; _usingMockData?: boolean }> => {
  console.log('✅ Validating layout:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/validate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Layout validation complete');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating validation');
  }

  await new Promise(resolve => setTimeout(resolve, 500));

  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const suggestions: string[] = [];

  // Fetch layout components for validation
  const sectionsResponse = await fetchLayoutSections(layoutId);
  const sections = sectionsResponse.data;

  // Simple validation checks
  if (sections.length === 0) {
    errors.push({
      severity: 'error',
      section: 'layout',
      message: 'Layout has no sections defined',
      fix: 'Add at least one section to the layout'
    });
  }

  sections.forEach(section => {
    if (section.fields.length === 0) {
      warnings.push({
        severity: 'warning',
        section: section.label,
        message: 'Section has no fields assigned',
      });
    }

    if (section.fields.length > 20) {
      warnings.push({
        severity: 'info',
        section: section.label,
        message: 'Section has many fields (>20), consider splitting',
      });
    }
  });

  suggestions.push('Add more sections for better organization');
  suggestions.push('Configure visibility rules for dynamic layouts');
  suggestions.push('Add related lists to show related records');

  const result: LayoutValidationResult = {
    isValid: errors.length === 0,
    errors,
    warnings,
    suggestions
  };

  return {
    data: result,
    _usingMockData: true
  };
};

// Clone layout
export const cloneLayout = async (
  cloneRequest: LayoutCloneRequest
): Promise<{ data: PageLayoutTemplate; message: string; _usingMockData?: boolean }> => {
  console.log('📋 Cloning layout:', cloneRequest);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(cloneRequest)
    });

    if (response.ok) {
      console.log('✅ Layout cloned successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating layout clone');
  }

  await new Promise(resolve => setTimeout(resolve, 600));

  // Find source layout
  const sourceLayout = MOCK_PAGE_LAYOUT_TEMPLATES.find((l: PageLayoutTemplate) => l.id === cloneRequest.sourceLayoutId);
  if (!sourceLayout) {
    throw new Error('Source layout not found');
  }

  // Create cloned layout
  const clonedLayout: PageLayoutTemplate = {
    ...sourceLayout,
    id: `layout_${Date.now()}`,
    name: cloneRequest.newName,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  MOCK_PAGE_LAYOUT_TEMPLATES.push(clonedLayout);

  // TODO: Clone sections, rules, related lists, actions based on flags

  return {
    data: clonedLayout,
    message: 'Layout cloned successfully',
    _usingMockData: true
  };
};

// Get layout health check
export const getLayoutHealthCheck = async (layoutId: string): Promise<{ data: LayoutHealthCheck; _usingMockData?: boolean }> => {
  console.log('🏥 Running health check for layout:', layoutId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/layouts/${layoutId}/health`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Health check complete');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating health check');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  // Fetch layout components
  const sectionsResponse = await fetchLayoutSections(layoutId);
  const sections = sectionsResponse.data;

  const rulesResponse = await fetchVisibilityRules(layoutId);
  const rules = rulesResponse.data;

  const relatedListsResponse = await fetchRelatedLists(layoutId);
  const relatedLists = relatedListsResponse.data;

  const quickActionsResponse = await fetchQuickActions(layoutId);
  const quickActions = quickActionsResponse.data;

  // Calculate metrics
  const fieldsCount = sections.reduce((sum, s) => sum + s.fields.length, 0);

  const metrics = {
    sectionsCount: sections.length,
    fieldsCount,
    rulesCount: rules.length,
    relatedListsCount: relatedLists.length,
    quickActionsCount: quickActions.length,
    unusedFields: 0,  // Would need to check against field usage
    conflictingRules: 0  // Would need to check rule conflicts
  };

  // Calculate health score (0-100)
  let score = 50;  // Base score

  if (sections.length > 0) score += 10;
  if (fieldsCount > 5) score += 10;
  if (fieldsCount > 15) score += 10;
  if (rules.length > 0) score += 10;
  if (relatedLists.length > 0) score += 5;
  if (quickActions.length > 0) score += 5;

  // Determine health level
  let health: 'excellent' | 'good' | 'fair' | 'poor';
  if (score >= 85) health = 'excellent';
  else if (score >= 70) health = 'good';
  else if (score >= 50) health = 'fair';
  else health = 'poor';

  const recommendations: string[] = [];

  if (sections.length === 0) {
    recommendations.push('Add sections to organize your layout');
  }
  if (fieldsCount < 10) {
    recommendations.push('Consider adding more fields to make the layout more useful');
  }
  if (rules.length === 0) {
    recommendations.push('Add visibility rules for dynamic layouts');
  }
  if (relatedLists.length === 0) {
    recommendations.push('Add related lists to show related records');
  }
  if (quickActions.length === 0) {
    recommendations.push('Add quick actions for common tasks');
  }

  const healthCheck: LayoutHealthCheck = {
    layoutId,
    health,
    score,
    metrics,
    recommendations
  };

  return {
    data: healthCheck,
    _usingMockData: true
  };
};

// ============================================
// DYNAMIC TABS API FUNCTIONS
// ============================================

// Fetch dynamic tabs for a page
export const fetchDynamicTabs = async (filters?: {
  page?: string;
  application?: Application;
}): Promise<{ data: DynamicTab[] }> => {
  console.log('🔍 Fetching dynamic tabs...', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page);
    if (filters?.application) params.append('application', filters.application);

    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/tabs?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Dynamic tabs loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock dynamic tabs');
  }

  await new Promise(resolve => setTimeout(resolve, 100));

  let filtered = [...MOCK_DYNAMIC_TABS];

  if (filters?.page) {
    filtered = filtered.filter(t => t.page === filters.page);
  }

  if (filters?.application) {
    filtered = filtered.filter(t => t.application === filters.application);
  }

  return { data: filtered };
};

// Create dynamic tab
export const createDynamicTab = async (
  tabData: Omit<DynamicTab, 'id'>
): Promise<{ data: DynamicTab; message: string }> => {
  console.log('✨ Creating dynamic tab:', tabData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/tabs`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(tabData)
    });

    if (response.ok) {
      console.log('✅ Dynamic tab created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating tab creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const newTab: DynamicTab = {
    ...tabData,
    id: `tab_${Date.now()}`
  };

  MOCK_DYNAMIC_TABS.push(newTab);

  return {
    data: newTab,
    message: 'Dynamic tab created successfully'
  };
};

// Update dynamic tab
export const updateDynamicTab = async (
  tabId: string,
  updates: Partial<Omit<DynamicTab, 'id'>>
): Promise<{ data: DynamicTab; message: string }> => {
  console.log('💾 Updating dynamic tab:', tabId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/tabs/${tabId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ Dynamic tab updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating tab update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const tabIndex = MOCK_DYNAMIC_TABS.findIndex(t => t.id === tabId);
  if (tabIndex === -1) {
    throw new Error('Tab not found');
  }

  const updatedTab: DynamicTab = {
    ...MOCK_DYNAMIC_TABS[tabIndex],
    ...updates
  };

  MOCK_DYNAMIC_TABS[tabIndex] = updatedTab;

  return {
    data: updatedTab,
    message: 'Dynamic tab updated successfully'
  };
};

// Delete dynamic tab
export const deleteDynamicTab = async (tabId: string): Promise<{ message: string }> => {
  console.log('🗑️ Deleting dynamic tab:', tabId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/page-layouts/tabs/${tabId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Dynamic tab deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating tab deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const tabIndex = MOCK_DYNAMIC_TABS.findIndex(t => t.id === tabId);
  if (tabIndex === -1) {
    throw new Error('Tab not found');
  }

  MOCK_DYNAMIC_TABS.splice(tabIndex, 1);

  return {
    message: 'Dynamic tab deleted successfully'
  };
};

// ============================================
// LIST VIEW TEMPLATES API FUNCTIONS
// ============================================

/**
 * Fetch all list view templates with optional filtering
 * @param filters - Optional filter criteria (application, entityType)
 * @returns Promise with filtered list view templates
 */
export const fetchListViewTemplates = async (filters?: {
  application?: Application;
  entityType?: string;
}): Promise<{ data: ListViewTemplate[] }> => {
  console.log('🔍 Fetching list view templates...', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.application) params.append('application', filters.application);
    if (filters?.entityType) params.append('entityType', filters.entityType);

    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ List view templates loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock list view templates');
  }

  // Mock data with filtering
  await new Promise(resolve => setTimeout(resolve, 150));

  let filtered = [...MOCK_LIST_VIEW_TEMPLATES];

  if (filters?.application) {
    filtered = filtered.filter(t => t.application === filters.application);
  }

  if (filters?.entityType) {
    filtered = filtered.filter(t => t.entityType === filters.entityType);
  }

  console.log('📦 List view templates fetched from MOCK DATA', { count: filtered.length });
  return { data: filtered };
};

/**
 * Fetch a single list view template by ID
 * @param templateId - The template ID to fetch
 * @returns Promise with the list view template
 */
export const fetchListViewTemplate = async (templateId: string): Promise<{ data: ListViewTemplate }> => {
  console.log('🔍 Fetching list view template:', templateId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates/${templateId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ List view template loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, using mock template');
  }

  await new Promise(resolve => setTimeout(resolve, 100));

  const template = MOCK_LIST_VIEW_TEMPLATES.find(t => t.id === templateId);
  if (!template) {
    throw new Error('List view template not found');
  }

  console.log('📦 List view template fetched from MOCK DATA');
  return { data: template };
};

/**
 * Create a new list view template
 * @param templateData - The template data to create
 * @returns Promise with the created template and success message
 */
export const createListViewTemplate = async (
  templateData: Omit<ListViewTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>
): Promise<{ data: ListViewTemplate; message: string }> => {
  console.log('✨ Creating list view template:', templateData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData)
    });

    if (response.ok) {
      console.log('✅ List view template created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template creation');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  // If this is set as default, unset other defaults for the same entity type
  if (templateData.isDefault) {
    MOCK_LIST_VIEW_TEMPLATES.forEach(t => {
      if (t.entityType === templateData.entityType && t.application === templateData.application) {
        t.isDefault = false;
      }
    });
  }

  const newTemplate: ListViewTemplate = {
    ...templateData,
    id: `listview_${Date.now()}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'current_user'
  };

  MOCK_LIST_VIEW_TEMPLATES.push(newTemplate);

  console.log('📦 List view template created in MOCK DATA');
  return {
    data: newTemplate,
    message: 'List view template created successfully'
  };
};

/**
 * Update an existing list view template
 * @param templateId - The template ID to update
 * @param updates - Partial template data to update
 * @returns Promise with the updated template and success message
 */
export const updateListViewTemplate = async (
  templateId: string,
  updates: Partial<Omit<ListViewTemplate, 'id' | 'createdAt' | 'createdBy'>>
): Promise<{ data: ListViewTemplate; message: string }> => {
  console.log('💾 Updating list view template:', templateId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates/${templateId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    if (response.ok) {
      console.log('✅ List view template updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template update');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const templateIndex = MOCK_LIST_VIEW_TEMPLATES.findIndex(t => t.id === templateId);
  if (templateIndex === -1) {
    throw new Error('List view template not found');
  }

  const currentTemplate = MOCK_LIST_VIEW_TEMPLATES[templateIndex];

  // If updating to default, unset other defaults for the same entity type
  if (updates.isDefault === true) {
    MOCK_LIST_VIEW_TEMPLATES.forEach(t => {
      if (t.id !== templateId && t.entityType === currentTemplate.entityType && t.application === currentTemplate.application) {
        t.isDefault = false;
      }
    });
  }

  const updatedTemplate: ListViewTemplate = {
    ...currentTemplate,
    ...updates,
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user'
  };

  MOCK_LIST_VIEW_TEMPLATES[templateIndex] = updatedTemplate;

  console.log('📦 List view template updated in MOCK DATA');
  return {
    data: updatedTemplate,
    message: 'List view template updated successfully'
  };
};

/**
 * Clone an existing list view template
 * @param templateId - The template ID to clone
 * @param newName - The name for the cloned template
 * @returns Promise with the cloned template and success message
 */
export const cloneListViewTemplate = async (
  templateId: string,
  newName: string
): Promise<{ data: ListViewTemplate; message: string }> => {
  console.log('📋 Cloning list view template:', templateId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates/${templateId}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name: newName })
    });

    if (response.ok) {
      console.log('✅ List view template cloned successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template cloning');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const originalTemplate = MOCK_LIST_VIEW_TEMPLATES.find(t => t.id === templateId);
  if (!originalTemplate) {
    throw new Error('List view template not found');
  }

  const clonedTemplate: ListViewTemplate = {
    ...originalTemplate,
    id: `listview_${Date.now()}`,
    name: newName,
    isDefault: false, // Cloned templates are never default
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'current_user',
    updatedBy: undefined
  };

  MOCK_LIST_VIEW_TEMPLATES.push(clonedTemplate);

  console.log('📦 List view template cloned in MOCK DATA');
  return {
    data: clonedTemplate,
    message: 'List view template cloned successfully'
  };
};

/**
 * Delete a list view template
 * @param templateId - The template ID to delete
 * @returns Promise with success message
 */
export const deleteListViewTemplate = async (templateId: string): Promise<{ message: string }> => {
  console.log('🗑️ Deleting list view template:', templateId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates/${templateId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ List view template deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating template deletion');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const templateIndex = MOCK_LIST_VIEW_TEMPLATES.findIndex(t => t.id === templateId);
  if (templateIndex === -1) {
    throw new Error('List view template not found');
  }

  MOCK_LIST_VIEW_TEMPLATES.splice(templateIndex, 1);

  console.log('📦 List view template deleted from MOCK DATA');
  return {
    message: 'List view template deleted successfully'
  };
};

/**
 * Fetch list view statistics
 * @returns Promise with list view statistics
 */
export const fetchListViewStatistics = async (): Promise<{ data: ListViewStatistics }> => {
  console.log('📊 Fetching list view statistics...');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/statistics`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ List view statistics loaded from backend');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, calculating statistics from mock data');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  const stats: ListViewStatistics = {
    totalTemplates: MOCK_LIST_VIEW_TEMPLATES.length,
    byApplication: {} as Record<Application, number>,
    byEntityType: {},
    defaultTemplates: MOCK_LIST_VIEW_TEMPLATES.filter(t => t.isDefault),
    recentlyCreated: [...MOCK_LIST_VIEW_TEMPLATES]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5),
    recentlyUpdated: [...MOCK_LIST_VIEW_TEMPLATES]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5)
  };

  // Count by application
  MOCK_LIST_VIEW_TEMPLATES.forEach(template => {
    stats.byApplication[template.application] = (stats.byApplication[template.application] || 0) + 1;
  });

  // Count by entity type
  MOCK_LIST_VIEW_TEMPLATES.forEach(template => {
    stats.byEntityType[template.entityType] = (stats.byEntityType[template.entityType] || 0) + 1;
  });

  console.log('📦 List view statistics calculated from MOCK DATA', stats);
  return { data: stats };
};

/**
 * Set a list view template as the default for its entity type
 * @param templateId - The template ID to set as default
 * @returns Promise with the updated template and success message
 */
export const setDefaultListViewTemplate = async (templateId: string): Promise<{ data: ListViewTemplate; message: string }> => {
  console.log('⭐ Setting list view template as default:', templateId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/list-views/templates/${templateId}/set-default`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ List view template set as default successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, simulating set default');
  }

  await new Promise(resolve => setTimeout(resolve, 250));

  const templateIndex = MOCK_LIST_VIEW_TEMPLATES.findIndex(t => t.id === templateId);
  if (templateIndex === -1) {
    throw new Error('List view template not found');
  }

  const template = MOCK_LIST_VIEW_TEMPLATES[templateIndex];

  // Unset all other defaults for this entity type and application
  MOCK_LIST_VIEW_TEMPLATES.forEach(t => {
    if (t.entityType === template.entityType && t.application === template.application) {
      t.isDefault = false;
    }
  });

  // Set this template as default
  const updatedTemplate: ListViewTemplate = {
    ...template,
    isDefault: true,
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user'
  };

  MOCK_LIST_VIEW_TEMPLATES[templateIndex] = updatedTemplate;

  console.log('📦 List view template set as default in MOCK DATA');
  return {
    data: updatedTemplate,
    message: 'List view template set as default successfully'
  };
};

// ===========================
// USER MANAGEMENT API FUNCTIONS
// ===========================

/**
 * API ENDPOINT: GET /api/admin/users
 * Fetch all users with optional filters
 */
export const fetchUsers = async (filters?: {
  role?: UserRole;
  status?: UserStatus;
  application?: Application;
  search?: string;
}): Promise<{ data: User[] }> => {
  console.log('📥 Fetching users with filters:', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.application) params.append('application', filters.application);
    if (filters?.search) params.append('search', filters.search);

    const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Users fetched from backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, returning MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  let filtered = [...MOCK_USERS];

  if (filters?.role) {
    filtered = filtered.filter(u => u.role === filters.role);
  }
  if (filters?.status) {
    filtered = filtered.filter(u => u.status === filters.status);
  }
  if (filters?.application) {
    const app = filters.application;
    filtered = filtered.filter(u => u.applications.includes(app));
  }
  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(u =>
      u.firstName.toLowerCase().includes(searchLower) ||
      u.lastName.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      u.username.toLowerCase().includes(searchLower)
    );
  }

  console.log('📦 Returning filtered MOCK_USERS:', filtered.length);
  return { data: filtered };
};

/**
 * API ENDPOINT: GET /api/admin/users/:userId
 * Fetch a single user by ID
 */
export const fetchUser = async (userId: string): Promise<{ data: User }> => {
  console.log('📥 Fetching user:', userId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ User fetched from backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, returning MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  const user = MOCK_USERS.find(u => u.id === userId);
  if (!user) {
    throw new Error('User not found');
  }

  console.log('📦 Returning user from MOCK_USERS');
  return { data: user };
};

/**
 * API ENDPOINT: POST /api/admin/users
 * Create a new user with validation
 */
export const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>): Promise<{ data: User; message: string }> => {
  console.log('📤 Creating new user:', userData);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    if (response.ok) {
      console.log('✅ User created successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, creating user in MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 400));

  // Validation: Check for duplicate username
  if (MOCK_USERS.some(u => u.username === userData.username)) {
    throw new Error('Username already exists');
  }

  // Validation: Check for duplicate email
  if (MOCK_USERS.some(u => u.email === userData.email)) {
    throw new Error('Email already exists');
  }

  const newUser: User = {
    id: `user_${String(MOCK_USERS.length + 1).padStart(3, '0')}`,
    ...userData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'current_user'
  };

  MOCK_USERS.push(newUser);

  console.log('📦 User created in MOCK_USERS');
  return {
    data: newUser,
    message: 'User created successfully'
  };
};

/**
 * API ENDPOINT: PUT /api/admin/users/:userId
 * Update an existing user
 */
export const updateUser = async (userId: string, updates: Partial<User>): Promise<{ data: User; message: string }> => {
  console.log('📤 Updating user:', userId, updates);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (response.ok) {
      console.log('✅ User updated successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, updating user in MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 400));

  const userIndex = MOCK_USERS.findIndex(u => u.id === userId);
  if (userIndex === -1) {
    throw new Error('User not found');
  }

  const currentUser = MOCK_USERS[userIndex];

  // Validation: Check for duplicate username if username is being changed
  if (updates.username && updates.username !== currentUser.username) {
    if (MOCK_USERS.some(u => u.username === updates.username)) {
      throw new Error('Username already exists');
    }
  }

  // Validation: Check for duplicate email if email is being changed
  if (updates.email && updates.email !== currentUser.email) {
    if (MOCK_USERS.some(u => u.email === updates.email)) {
      throw new Error('Email already exists');
    }
  }

  const updatedUser: User = {
    ...currentUser,
    ...updates,
    id: userId,  // Ensure ID doesn't change
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user'
  };

  MOCK_USERS[userIndex] = updatedUser;

  console.log('📦 User updated in MOCK_USERS');
  return {
    data: updatedUser,
    message: 'User updated successfully'
  };
};

/**
 * API ENDPOINT: DELETE /api/admin/users/:userId
 * Delete a user (with system admin protection)
 */
export const deleteUser = async (userId: string): Promise<{ message: string }> => {
  console.log('🗑️ Deleting user:', userId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ User deleted successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, deleting user from MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const userIndex = MOCK_USERS.findIndex(u => u.id === userId);
  if (userIndex === -1) {
    throw new Error('User not found');
  }

  const user = MOCK_USERS[userIndex];

  // Validation: Cannot delete system admin
  if (user.role === 'system_admin') {
    throw new Error('Cannot delete system administrator');
  }

  MOCK_USERS.splice(userIndex, 1);

  console.log('📦 User deleted from MOCK_USERS');
  return { message: 'User deleted successfully' };
};

/**
 * API ENDPOINT: POST /api/admin/users/:userId/reset-password
 * Reset user password and optionally force password change
 */
export const resetUserPassword = async (userId: string, mustChangePassword: boolean = true): Promise<{ message: string }> => {
  console.log('🔑 Resetting password for user:', userId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ mustChangePassword }),
    });

    if (response.ok) {
      console.log('✅ Password reset successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, resetting password in MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const userIndex = MOCK_USERS.findIndex(u => u.id === userId);
  if (userIndex === -1) {
    throw new Error('User not found');
  }

  const user = MOCK_USERS[userIndex];
  MOCK_USERS[userIndex] = {
    ...user,
    mustChangePassword,
    passwordLastChangedAt: new Date().toISOString(),
    failedLoginAttempts: 0,
    updatedAt: new Date().toISOString(),
    updatedBy: 'current_user'
  };

  console.log('📦 Password reset in MOCK_USERS');
  return { message: 'Password reset successfully' };
};

/**
 * API ENDPOINT: POST /api/admin/users/bulk-operation
 * Perform bulk operations on multiple users
 */
export const bulkUserOperation = async (operation: BulkUserOperation): Promise<{ message: string; affectedCount: number }> => {
  console.log('🔄 Performing bulk operation:', operation);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/bulk-operation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(operation),
    });

    if (response.ok) {
      console.log('✅ Bulk operation completed successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, performing bulk operation in MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 500));

  let affectedCount = 0;

  operation.userIds.forEach(userId => {
    const userIndex = MOCK_USERS.findIndex(u => u.id === userId);
    if (userIndex === -1) return;

    const user = MOCK_USERS[userIndex];

    // Prevent operations on system admin
    if (user.role === 'system_admin' && ['delete', 'deactivate', 'lock'].includes(operation.operation)) {
      console.warn(`⚠️ Skipping ${operation.operation} on system admin`);
      return;
    }

    switch (operation.operation) {
      case 'activate':
        MOCK_USERS[userIndex] = { ...user, status: 'active', updatedAt: new Date().toISOString() };
        affectedCount++;
        break;
      case 'deactivate':
        MOCK_USERS[userIndex] = { ...user, status: 'inactive', updatedAt: new Date().toISOString() };
        affectedCount++;
        break;
      case 'lock':
        MOCK_USERS[userIndex] = { ...user, status: 'locked', updatedAt: new Date().toISOString() };
        affectedCount++;
        break;
      case 'unlock':
        MOCK_USERS[userIndex] = { ...user, status: 'active', failedLoginAttempts: 0, updatedAt: new Date().toISOString() };
        affectedCount++;
        break;
      case 'delete':
        MOCK_USERS.splice(userIndex, 1);
        affectedCount++;
        break;
      case 'assign_role':
        if (operation.newRole) {
          MOCK_USERS[userIndex] = { ...user, role: operation.newRole, updatedAt: new Date().toISOString() };
          affectedCount++;
        }
        break;
    }
  });

  console.log('📦 Bulk operation completed in MOCK_USERS');
  return {
    message: `Bulk operation completed: ${affectedCount} users affected`,
    affectedCount
  };
};

/**
 * API ENDPOINT: GET /api/admin/users/statistics
 * Fetch user statistics
 */
export const fetchUserStatistics = async (): Promise<{ data: UserStatistics }> => {
  console.log('📊 Fetching user statistics');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/statistics`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ User statistics fetched from backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, calculating statistics from MOCK_USERS');
  }

  await new Promise(resolve => setTimeout(resolve, 300));

  const statistics: UserStatistics = {
    totalUsers: MOCK_USERS.length,
    activeUsers: MOCK_USERS.filter(u => u.status === 'active').length,
    inactiveUsers: MOCK_USERS.filter(u => u.status === 'inactive').length,
    lockedUsers: MOCK_USERS.filter(u => u.status === 'locked').length,
    pendingUsers: MOCK_USERS.filter(u => u.status === 'pending').length,
    byRole: {
      system_admin: MOCK_USERS.filter(u => u.role === 'system_admin').length,
      cs_supervisor: MOCK_USERS.filter(u => u.role === 'cs_supervisor').length,
      cs_analyst: MOCK_USERS.filter(u => u.role === 'cs_analyst').length,
      credit_manager: MOCK_USERS.filter(u => u.role === 'credit_manager').length,
      credit_analyst: MOCK_USERS.filter(u => u.role === 'credit_analyst').length,
      compliance_officer: MOCK_USERS.filter(u => u.role === 'compliance_officer').length,
      viewer: MOCK_USERS.filter(u => u.role === 'viewer').length
    },
    byApplication: {
      global: MOCK_USERS.filter(u => u.applications.includes('global')).length,
      cybersweep: MOCK_USERS.filter(u => u.applications.includes('cybersweep')).length,
      'credit-repair': MOCK_USERS.filter(u => u.applications.includes('credit-repair')).length,
      'id-theft': MOCK_USERS.filter(u => u.applications.includes('id-theft')).length,
      'credit-development': MOCK_USERS.filter(u => u.applications.includes('credit-development')).length,
      'credit-block': MOCK_USERS.filter(u => u.applications.includes('credit-block')).length,
      'regal-main': MOCK_USERS.filter(u => u.applications.includes('regal-main')).length
    },
    recentLogins: MOCK_USER_ACTIVITY.filter(a => a.action === 'login').slice(0, 10),
    recentActivity: MOCK_USER_ACTIVITY.slice(0, 20)
  };

  console.log('📦 User statistics calculated from MOCK_USERS');
  return { data: statistics };
};

/**
 * API ENDPOINT: GET /api/admin/users/:userId/activity
 * Fetch activity logs for a specific user
 */
export const fetchUserActivity = async (userId: string, limit: number = 50): Promise<{ data: UserActivityLog[] }> => {
  console.log('📥 Fetching activity for user:', userId);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/activity?limit=${limit}`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ User activity fetched from backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, returning MOCK_USER_ACTIVITY');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  const activity = MOCK_USER_ACTIVITY
    .filter(a => a.userId === userId)
    .slice(0, limit);

  console.log('📦 Returning user activity from MOCK_USER_ACTIVITY');
  return { data: activity };
};

// ===========================
// THEME & SETTINGS API
// ===========================

/**
 * Fetch application settings
 * Returns the current application settings for theme, display, notifications, security, and system
 */
export const fetchSettings = async (): Promise<{ data: ApplicationSettings }> => {
  console.log('📥 Fetching application settings');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Settings fetched from backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, returning MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  console.log('📦 Returning settings from MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Update theme settings
 * Updates theme-related settings (mode, color scheme, font size, etc.)
 */
export const updateThemeSettings = async (
  themeSettings: Partial<ThemeSettings>,
  updatedBy?: string
): Promise<{ data: ApplicationSettings }> => {
  console.log('💾 Updating theme settings:', themeSettings);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/theme`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(themeSettings),
    });

    if (response.ok) {
      console.log('✅ Theme settings updated on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, updating MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  // Update mock data in-memory
  MOCK_SETTINGS.theme = {
    ...MOCK_SETTINGS.theme,
    ...themeSettings
  };
  MOCK_SETTINGS.lastUpdated = new Date().toISOString();
  MOCK_SETTINGS.updatedBy = updatedBy || 'user_001';

  console.log('📦 Theme settings updated in MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Update display settings
 * Updates display-related settings (date format, timezone, number format, etc.)
 */
export const updateDisplaySettings = async (
  displaySettings: Partial<DisplaySettings>,
  updatedBy?: string
): Promise<{ data: ApplicationSettings }> => {
  console.log('💾 Updating display settings:', displaySettings);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/display`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(displaySettings),
    });

    if (response.ok) {
      console.log('✅ Display settings updated on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, updating MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  // Update mock data in-memory
  MOCK_SETTINGS.display = {
    ...MOCK_SETTINGS.display,
    ...displaySettings
  };
  MOCK_SETTINGS.lastUpdated = new Date().toISOString();
  MOCK_SETTINGS.updatedBy = updatedBy || 'user_001';

  console.log('📦 Display settings updated in MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Update notification settings
 * Updates notification preferences (email, push, desktop, etc.)
 */
export const updateNotificationSettings = async (
  notificationSettings: Partial<NotificationSettings>,
  updatedBy?: string
): Promise<{ data: ApplicationSettings }> => {
  console.log('💾 Updating notification settings:', notificationSettings);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/notifications`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(notificationSettings),
    });

    if (response.ok) {
      console.log('✅ Notification settings updated on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, updating MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  // Update mock data in-memory
  MOCK_SETTINGS.notifications = {
    ...MOCK_SETTINGS.notifications,
    ...notificationSettings
  };
  MOCK_SETTINGS.lastUpdated = new Date().toISOString();
  MOCK_SETTINGS.updatedBy = updatedBy || 'user_001';

  console.log('📦 Notification settings updated in MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Update security settings
 * Updates security-related settings (session timeout, MFA, password policies, etc.)
 */
export const updateSecuritySettings = async (
  securitySettings: Partial<SecuritySettings>,
  updatedBy?: string
): Promise<{ data: ApplicationSettings }> => {
  console.log('💾 Updating security settings:', securitySettings);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/security`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(securitySettings),
    });

    if (response.ok) {
      console.log('✅ Security settings updated on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, updating MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  // Update mock data in-memory
  MOCK_SETTINGS.security = {
    ...MOCK_SETTINGS.security,
    ...securitySettings
  };
  MOCK_SETTINGS.lastUpdated = new Date().toISOString();
  MOCK_SETTINGS.updatedBy = updatedBy || 'user_001';

  console.log('📦 Security settings updated in MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Update system settings
 * Updates system-related settings (language, auto-save, analytics, maintenance mode, etc.)
 */
export const updateSystemSettings = async (
  systemSettings: Partial<SystemSettings>,
  updatedBy?: string
): Promise<{ data: ApplicationSettings }> => {
  console.log('💾 Updating system settings:', systemSettings);

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/system`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(systemSettings),
    });

    if (response.ok) {
      console.log('✅ System settings updated on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, updating MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  // Update mock data in-memory
  MOCK_SETTINGS.system = {
    ...MOCK_SETTINGS.system,
    ...systemSettings
  };
  MOCK_SETTINGS.lastUpdated = new Date().toISOString();
  MOCK_SETTINGS.updatedBy = updatedBy || 'user_001';

  console.log('📦 System settings updated in MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Reset all settings to defaults
 * Resets all application settings back to their default values
 */
export const resetSettingsToDefaults = async (updatedBy?: string): Promise<{ data: ApplicationSettings }> => {
  console.log('🔄 Resetting all settings to defaults');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/reset`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Settings reset on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, resetting MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  // Reset mock data to defaults
  MOCK_SETTINGS = {
    theme: {
      mode: 'auto',
      colorScheme: 'blue',
      fontSize: 'medium',
      compactMode: false,
      highContrast: false,
      reducedMotion: false
    },
    display: {
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      timezone: 'America/New_York',
      numberFormat: 'en-US',
      currencySymbol: '$',
      itemsPerPage: 25,
      showGridLines: true,
      defaultView: 'list'
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      desktopNotifications: false,
      soundEnabled: true,
      notifyOnTaskAssignment: true,
      notifyOnTaskCompletion: true,
      notifyOnMention: true,
      notifyOnComment: false,
      dailyDigestEnabled: false,
      dailyDigestTime: '09:00'
    },
    security: {
      sessionTimeout: 30,
      mfaEnabled: false,
      passwordExpiryDays: 90,
      loginNotifications: true,
      allowedIpAddresses: [],
      requirePasswordChange: false,
      lockoutThreshold: 5,
      lockoutDuration: 15
    },
    system: {
      language: 'en-US',
      autoSaveEnabled: true,
      autoSaveInterval: 30,
      debugMode: false,
      analyticsEnabled: true,
      errorReportingEnabled: true,
      maintenanceMode: false,
      backupFrequency: 'daily',
      backupTime: '02:00'
    },
    lastUpdated: new Date().toISOString(),
    updatedBy: updatedBy || 'user_001'
  };

  console.log('📦 Settings reset to defaults in MOCK_SETTINGS');
  return { data: MOCK_SETTINGS };
};

/**
 * Export settings
 * Exports all application settings as a JSON string for backup/migration
 */
export const exportSettings = async (): Promise<{ data: string }> => {
  console.log('📤 Exporting settings');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/export`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      console.log('✅ Settings exported from backend successfully');
      const result = await response.json();
      return { data: result.data };
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, exporting MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  const exportData = JSON.stringify(MOCK_SETTINGS, null, 2);
  console.log('📦 Settings exported from MOCK_SETTINGS');
  return { data: exportData };
};

/**
 * Import settings
 * Imports settings from a JSON string (from backup/migration)
 */
export const importSettings = async (settingsJson: string, updatedBy?: string): Promise<{ data: ApplicationSettings }> => {
  console.log('📥 Importing settings');

  try {
    const response = await fetch(`${API_BASE_URL}/admin/settings/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ settings: settingsJson }),
    });

    if (response.ok) {
      console.log('✅ Settings imported on backend successfully');
      return await response.json();
    }
  } catch (error) {
    console.warn('⚠️ Backend unavailable, importing to MOCK_SETTINGS');
  }

  await new Promise(resolve => setTimeout(resolve, 200));

  try {
    const importedSettings = JSON.parse(settingsJson) as ApplicationSettings;

    // Update mock data with imported settings
    MOCK_SETTINGS = {
      ...importedSettings,
      lastUpdated: new Date().toISOString(),
      updatedBy: updatedBy || 'user_001'
    };

    console.log('📦 Settings imported to MOCK_SETTINGS');
    return { data: MOCK_SETTINGS };
  } catch (parseError) {
    console.error('❌ Failed to parse settings JSON:', parseError);
    throw new Error('Invalid settings JSON format');
  }
};
